import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Camera, Users, FileText, ChevronDown, X, Mail, Send, 
  QrCodeIcon, Bell, UserCog, LogIn, Layout, Sparkles, 
  Home, Laptop, Smartphone, User, ExternalLink, Play, Download, 
  AlertCircle, Menu, Shield, Key, Database, BookOpen, HelpCircle, Heart, Settings,
  FileText as FileTextIcon, Lock, Layers, ArrowUp, ArrowDown, Hash, Clock, Activity, Check } from 'lucide-react';
import EdgeCompatibleVideoPlayer from './EdgeCompatibleVideoPlayer';
import UserManagementDashboard from './UserManagementDashboard';

import videophone1 from "./assets/img/video1.webm";
import introVideo from "./assets/img/intro.mp4"; // Import the intro video

const LandingPage = () => {
  // States
  const [isLoaded, setIsLoaded] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeFeature, setActiveFeature] = useState(null);
  const [isPhoneMinimized, setIsPhoneMinimized] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showPhone, setShowPhone] = useState(false);
  const [showModalAnimation, setShowModalAnimation] = useState(false);
  const [hoveredVideo, setHoveredVideo] = useState(null);
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [scrollIndicatorVisible, setScrollIndicatorVisible] = useState(true);
  const [lastScrollProgress, setLastScrollProgress] = useState(0);
  const [emailError, setEmailError] = useState('');
  const [downloadStarted, setDownloadStarted] = useState(false);
  const [showBreakingNews, setShowBreakingNews] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [email, setEmail] = useState('');
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState('');
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [showMobileView, setShowMobileView] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  const [activeSection, setActiveSection] = useState('home');
  const [showSectionIndicator, setShowSectionIndicator] = useState(false);
  const [expandedProfileSection, setExpandedProfileSection] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const userMenuRef = useRef(null);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
const [showTerms, setShowTerms] = useState(false);
  const [currentSectionTitle, setCurrentSectionTitle] = useState('');
  const [showSectionTitle, setShowSectionTitle] = useState(false);

  // States for carousel
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [isCarouselLocked, setIsCarouselLocked] = useState(false);
  const [carouselScrolling, setCarouselScrolling] = useState(false);
  const [scrollDirection, setScrollDirection] = useState(null);
  const [cardsRevealed, setCardsRevealed] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(0);
  const [initialReveal, setInitialReveal] = useState(false);
  const [cardHovered, setCardHovered] = useState(false);
  const [buttonHovered, setButtonHovered] = useState(null);

  const [profileImage, setProfileImage] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userMenuAnimation, setUserMenuAnimation] = useState(false);
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);
  const [notificationCount, setNotificationCount] = useState(2);
  
  // Additional states for real backend integration in Profile section
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeCamp, setActiveCamp] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [nextAppointment, setNextAppointment] = useState(null);
  const [timeLeft, setTimeLeft] = useState('');
  const [profileImageLoading, setProfileImageLoading] = useState(false);
  const [profileImageError, setProfileImageError] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Refs
  const breakingNewsRef = useRef(null);
  const phoneRef = useRef(null);
  const explosionRef = useRef(null);
  const titleRef = useRef(null);
  const featuresRef = useRef(null);
  const homeRef = useRef(null);
  const userProfileRef = useRef(null);
  const carouselRef = useRef(null);
  const carouselSectionRef = useRef(null);
  const featuresListRef = useRef(null);
  const downloadRef = useRef(null);
  const contactRef = useRef(null);
  const headerRef = useRef(null);
  const animationTriggeredRef = useRef(false);

  // API Configuration for profile section
  const API_BASE_URL = 'https://lager-app-api.sunrise-avengers.ch:443';
  
  // Sections for navigation
  const sections = [
    { id: 'home', name: 'Home', icon: <Home size={18} />, ref: homeRef },
    { id: 'user-profile', name: 'User Profile', icon: <User size={18} />, ref: userProfileRef },
    { id: 'download', name: 'Download', icon: <Download size={18} />, ref: downloadRef },
    { id: 'features', name: 'Features', icon: <Layers size={18} />, ref: featuresListRef },
    { id: 'contact', name: 'Contact', icon: <Mail size={18} />, ref: contactRef }
  ];

  // Profile cards data
  const profileCards = [
    {
      id: 'login',
      title: 'Login & Register',
      icon: <LogIn size={24} color="#FFFFFF" />,
      color: '#1967D2',
      description: 'Access your personal camp account',
      details: 'Quickly log in to access your schedules, notifications, and personal data.',
      buttonText: 'Access Account',
      buttonIcon: <LogIn size={18} />
    },
    {
      id: 'profile',
      title: 'Edit Profile',
      icon: <UserCog size={24} color="#FFFFFF" />,
      color: '#FF3B30',
      description: 'Customize your personal information',
      details: 'Update your contact details, preferences, and profile picture.',
      buttonText: 'Edit Profile',
      buttonIcon: <UserCog size={18} />
    },
    {
      id: 'camp-info',
      title: 'Camp Information',
      icon: <BookOpen size={24} color="#FFFFFF" />,
      color: '#5856D6',
      description: 'Learn about our camp activities',
      details: 'Discover what makes our camp special, upcoming events, and our mission.',
      buttonText: 'See Details',
      buttonIcon: <BookOpen size={18} />
    },
    {
      id: 'privacy',
      title: 'Privacy Policy',
      icon: <Lock size={24} color="#FFFFFF" />,
      color: '#007AFF',
      description: 'How we protect your data',
      details: 'Review our data handling practices and privacy protection measures.',
      buttonText: 'Read Policy',
      buttonIcon: <Lock size={18} />
    }
  ];
  
  // Features Data
  const features = [
    {
      title: "Welcome & Login",
      description: "Your personalized access to the camp experience - simple and secure",
      icon: <LogIn size={28} />,
      video: "/assets/img/login.webm",
      oldVideo: "/assets/img/EEZM4765.webm",
      color: "#FF3B30",
      details: [
        "Quick login with existing account",
        "Easy registration for new participants",
        "Personal profile customization"
      ],
      whatsnew: [
        "Completely redesigned user interface for better clarity",
        "New visual indicators for login status",
        "Improved error handling with clear feedback",
        "Smoother transitions and animations"
      ]
    },
    {
      title: "Home Screen",
      description: "Your dashboard with all essential information at a glance",
      icon: <Home size={28} />,
      video: "/assets/img/home.webm",
      oldVideo: "/assets/img/Screen_Recording_20241204_092826_Expo Go (2).webm",
      color: "#FF3B30",
      details: [
        "Events and schedules",
        "Quick access to daily plan",
        "Important announcements overview"
      ],
      whatsnew: [
        "New UI with improved event visibility",
        "Added dynamic mascot that adapts to scheduled events",
        "Enhanced daily overview with all events visible",
        "Next event preview with countdown timer"
      ]
    },
    {
      title: "Profile Management",
      description: "Manage your personal data independently and hassle-free",
      icon: <UserCog size={28} />,
      video: "/assets/img/profil.webm",
      oldVideo: "/assets/img/HAAF2849.webm",
      color: "#FF2D55",
      details: [
        "Flexible data updates",
        "Profile picture and account settings",
        "Clear data management"
      ],
      whatsnew: [
        "New password change functionality",
        "Enhanced security settings",
        "Improved profile editing interface",
        "Added data privacy controls"
      ]
    },
    {
      title: "Quick Check-Out",
      description: "Saying goodnight has never been easier - with your personal QR code",
      icon: <QrCodeIcon size={28} />,
      video: "/assets/img/quickout.webm",
      oldVideo: "/assets/img/HGOGE4180.webm",
      color: "#FF375F",
      details: [
        "Fast check-out via QR code",
        "Direct confirmation by supervisors",
        "No more paper lists needed"
      ],
      whatsnew: [
        "Improved QR code scanning speed",
        "Enhanced confirmation system",
        "Better supervisor integration",
        "Real-time status updates"
      ]
    },
    {
      title: "Smart Notch",
      description: "Intelligent information display for quick overview",
      icon: <Layout size={28} />,
      video: "/assets/img/notch.webm",
      oldVideo: "/assets/img/WBDR4709.webm",
      color: "#FF6575",
      details: [
        "Next events at a glance",
        "Expandable schedule overview",
        "Important alerts in focus"
      ],
      whatsnew: [
        "Redesigned UI for better visibility",
        "Added support for all daily activities",
        "Improved next event preview",
        "New adaptive mascot integration"
      ]
    },
    {
      title: "Schedule & Events",
      description: "Your digital camp planner with all important dates and events",
      icon: <Calendar size={28} />,
      video: "/assets/img/dalyplanner.webm",
      oldVideo: "/assets/img/VHYG4124.webm",
      color: "#FF5B65",
      details: [
        "Daily overview of all activities",
        "Detailed timetables",
        "Easy schedule navigation"
      ],
      whatsnew: [
        "Completely redesigned UI",
        "All days now visible and accessible",
        "Enhanced event details display",
        "New mascot interactions for events"
      ]
    },
    {
      title: "Persons In Charge",
      description: "All contacts and supervisors at a glance",
      icon: <Users size={28} />,
      video: "/assets/img/PNQY3018 (1).webm",
      oldVideo: "/assets/img/REZF5593.webm",
      color: "#FF4F55",
      details: [
        "Complete overview of all vocational trainers",
        "Videos on every vocational trainer",
        "Clear responsibilities overview"
      ],
      whatsnew: [
        "Redesigned UI for better organization",
        "Improved contact information display",
        "Enhanced supervisor profiles",
        "Quick access to important contacts"
      ]
    },
    {
      title: "Alert System",
      description: "Comprehensive alert management for camp supervisors",
      icon: <Bell size={28} />,
      video: "/assets/img/alert.webm",
      oldVideo: "/assets/img/WBDR4709.webm",
      color: "#FF3B30",
      details: [
        "Create and manage various alert types",
        "Direct notifications to participants",
        "Schedule-linked alerts",
        "Emergency broadcast system"
      ],
      whatsnew: [
        "New alert creation system for supervisors",
        "Multiple alert types (Info, Important, Schedule)",
        "Direct schedule appointment linking",
        "Push notifications on mobile devices",
        "Customizable alert priorities"
      ]
    },
    {
      title: "Admin Dashboard",
      description: "Comprehensive management tool for camp supervisors",
      icon: <Layout size={28} />,
      video: "/assets/img/drawer.webm",         // Mobile version
      laptopVideo: "/assets/img/adminlaptop.webm", // Desktop version
      oldVideo: "/assets/img/drawer.webm",
      oldLaptopVideo: "/assets/img/adminlaptop.webm", 
      color: "#FF2D55",
      details: [
        "Complete camp management",
        "Participant oversight",
        "Schedule administration",
        "Alert system control",
        "Real-time monitoring"
      ],
      whatsnew: [
        "New management interface for supervisors",
        "Complete trainee overview and control",
        "Camp-wide schedule management",
        "Comprehensive monitoring tools",
        "Advanced reporting features"
      ]
    },
    {
      title: "Camp Phases",
      description: "Access different camp phases with phase-specific features and information",
      icon: <Calendar size={28} />,
      video: "/assets/img/vorlager.webm",
      oldVideo: "/assets/img/vorlager.webm",
      color: "#FF4F55",
      details: [
        "Pre-camp registration and preparation",
        "Packing list management",
        "Countdown to camp start",
        "Post-camp activities overview",
        "Phase-specific information access"
      ],
      whatsnew: [
        "Pre-camp registration and login system",
        "Interactive packing list with status tracking",
        "Dynamic countdown timer to camp start",
        "Post-camp activity schedule and memories",
        "Phase-based UI adaptations"
      ]
    },
    {
      title: "Drawer Menü",
      description: "Intelligentes und anpassbares Menüsystem für alle Benutzerrollen",
      icon: <Menu size={28} />,
      video: "/assets/img/KOXP1592.webm",
      oldVideo: "/assets/img/MAHK6485.webm",
      color: "#FF7585",
      details: [
        "Minimaler Zustand: Drei wichtigste Schaltflächen sichtbar",
        "Mittlerer Zustand: Standard-Menüpunkte",
        "Admin-Zugang: Zusätzliche Menüoptionen für Supervisoren",
        "Maximale Ansicht: Schnelle Übersicht über den aktuellen Termin"
      ],
      whatsnew: [
        "Neue Mehrstufigkeit (minimal, mittel, maximal)",
        "Admin-spezifische Funktionen im Drawer",
        "Vereinfachter Zugriff auf Hauptfunktionen im minimalen Modus",
        "Integrierte Terminanzeige im maximalen Zustand",
        "Modernisierte und benutzerfreundliche Menüstruktur"
      ]
    },
    {
      title: "Coming Soon",
      description: "Stay tuned for exciting new features!",
      icon: <Sparkles size={28} />,
      video: "/assets/img/soon.webm",
      oldvideo: "/assets/img/soon.webm",
      color: "#FF7585",
      details: [
        "More features in development",
        "Enhanced camp experience",
        "Continuous improvements"
      ],
      whatsnew: [
        "More features in development",
        "Enhanced camp experience",
        "Continuous improvements"
      ]
    }
  ];

  const userQuickActions = [
    {
      id: 'edit-profile',
      title: 'Edit Profile',
      icon: <UserCog size={16} color="#FF3B30" />,
      action: () => handleUserIconClick()
    },
    {
      id: 'view-schedule',
      title: 'Schedule',
      icon: <Calendar size={16} color="#34C759" />,
      action: () => handleUserIconClick()
    },
    {
      id: 'notifications',
      title: 'Notifications',
      icon: <Bell size={16} color="#FF9500" />,
      badge: notificationCount,
      action: () => handleUserIconClick()
    },
    {
      id: 'settings',
      title: 'Settings',
      icon: <Settings size={16} color="#5856D6" />,
      action: () => handleUserIconClick()
    },
    {
      id: 'logout',
      title: 'Logout',
      icon: <LogIn size={16} color="#FF2D55" />,
      action: () => {
        setIsUserLoggedIn(false);
        setUserData(null);
        setProfileImage(null);
        setShowUserMenu(false);
      }
    }
  ];

  // API helper function for profile section
  const api = async (endpoint, method = 'GET', data = null, headers = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const defaultHeaders = {
      'Content-Type': 'application/json',
      ...(localStorage.getItem('auth_token') ? {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
      } : {})
    };
    
    const requestOptions = {
      method,
      headers: { ...defaultHeaders, ...headers },
      ...(data && method !== 'GET' ? { body: JSON.stringify(data) } : {})
    };
    
    try {
      const response = await fetch(url, requestOptions);
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Something went wrong');
      }
      
      return result;
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  };
  
  // Load profile image with cache busting
  const loadProfileImage = (imagePath) => {
    if (!imagePath) return;
    
    setProfileImageLoading(true);
    setProfileImageError(false);
    
    // Add timestamp for cache busting
    const timestamp = new Date().getTime();
    const imageUrl = `${API_BASE_URL}${imagePath}?t=${timestamp}`;
    
    const img = new Image();
    
    img.onload = () => {
      setProfileImage(imageUrl);
      setProfileImageLoading(false);
    };
    
    img.onerror = () => {
      setProfileImageError(true);
      setProfileImageLoading(false);
    };
    
    img.src = imageUrl;
  };
  
  // Fetch active camp and appointments
  const fetchActiveCamp = async () => {
    try {
      setLoading(true);
      
      // First try to get current camp
      try {
        const data = await api('/api/camps/current');
        
        // Check camp phase
        const now = new Date();
        let currentPhase = null;
        
        if (data.phases) {
          currentPhase = data.phases.find(phase => {
            const phaseStart = new Date(phase.startDate);
            const phaseEnd = new Date(phase.endDate);
            return now >= phaseStart && now <= phaseEnd && phase.enabled;
          });
        }
        
        setActiveCamp({
          ...data,
          currentPhase: currentPhase
        });
        
        // If camp has schedule, format it for our use - but only if we're in MAIN_CAMP phase
        if (data && data.schedule && Array.isArray(data.schedule) && 
            currentPhase && currentPhase.phaseType === 'MAIN_CAMP') {
          const formattedAppointments = data.schedule
            .filter(item => item.date && item.title)
            .map(item => ({
              id: item.id || Math.random().toString(36).substring(2, 11),
              label: item.title,
              time: new Date(`${item.date}T${item.startTime || '00:00'}`),
              duration: item.endTime ? 
                (new Date(`${item.date}T${item.endTime}`).getTime() - 
                 new Date(`${item.date}T${item.startTime}`).getTime()) / (1000 * 60) : 60,
              location: item.location || '',
              description: item.description || ''
            }))
            .sort((a, b) => new Date(a.time) - new Date(b.time));
            
          setAppointments(formattedAppointments);
          
          // Set next appointment
          const now = new Date();
          const next = formattedAppointments.find(app => {
            const appTime = new Date(app.time);
            return appTime > now;
          });
          
          if (next) {
            setNextAppointment(next);
            updateTimeLeftForAppointment(next);
          }
        }
        
        setLoading(false);
        return;
      } catch (error) {
        console.warn('No current camp found, checking all camps...');
      }
      
      // If no current camp or error, try to get all camps
      const allCampsResponse = await api('/api/camps/all');
      const allCamps = allCampsResponse;
      
      const now = new Date();
      
      // Find the camp that is currently active
      const activeCamp = allCamps.find(camp => {
        const startDate = new Date(camp.startDate);
        const endDate = new Date(camp.endDate);
        return now >= startDate && now <= endDate;
      });
      
      if (activeCamp) {
        // Check camp phase
        let currentPhase = null;
        
        if (activeCamp.phases) {
          currentPhase = activeCamp.phases.find(phase => {
            const phaseStart = new Date(phase.startDate);
            const phaseEnd = new Date(phase.endDate);
            return now >= phaseStart && now <= phaseEnd && phase.enabled;
          });
        }
        
        setActiveCamp({
          ...activeCamp,
          currentPhase: currentPhase
        });
        
        // Similar processing for appointments as above
        if (activeCamp.schedule && Array.isArray(activeCamp.schedule) && 
            currentPhase && currentPhase.phaseType === 'MAIN_CAMP') {
          // Process schedule data similar to above
          const formattedAppointments = activeCamp.schedule
            .filter(item => item.date && item.title)
            .map(item => ({
              id: item.id || Math.random().toString(36).substring(2, 11),
              label: item.title,
              time: new Date(`${item.date}T${item.startTime || '00:00'}`),
              duration: item.endTime ? 
                (new Date(`${item.date}T${item.endTime}`).getTime() - 
                 new Date(`${item.date}T${item.startTime}`).getTime()) / (1000 * 60) : 60,
              location: item.location || '',
              description: item.description || ''
            }))
            .sort((a, b) => new Date(a.time) - new Date(b.time));
            
          setAppointments(formattedAppointments);
          
          // Set next appointment
          const now = new Date();
          const next = formattedAppointments.find(app => {
            const appTime = new Date(app.time);
            return appTime > now;
          });
          
          if (next) {
            setNextAppointment(next);
            updateTimeLeftForAppointment(next);
          }
        }
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching camp data:', err);
      setLoading(false);
    }
  };
  
  // Update time left for next appointment
  const updateTimeLeftForAppointment = (appointment) => {
    if (!appointment) return;
    
    const now = new Date();
    const appTime = new Date(appointment.time);
    const timeUntil = appTime - now;
    const hours = Math.floor(timeUntil / 3600000);
    const minutes = Math.floor((timeUntil % 3600000) / 60000);
    
    setTimeLeft(hours > 0 
      ? `In ${hours}h ${minutes}min` 
      : `In ${minutes} minutes`
    );
  };
  
  // Format time
  const formatTime = (time) => {
    const date = new Date(time);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // Handle user icon click to open management dashboard
  const handleUserIconClick = () => {
    console.log("User icon clicked");
    setShowUserManagement(true);
    // Prevent scrolling on the body while modal is open
    document.body.style.overflow = 'hidden';
  };

  // Handle login status change from UserManagementDashboard
  const handleLoginStatusChange = (isLoggedIn, user) => {
    setIsUserLoggedIn(isLoggedIn);
    setUserData(user);
    
    // If user is logged in, load their data
    if (isLoggedIn && user) {
      // Load profile image if available
      if (user.profileImagePath) {
        loadProfileImage(user.profileImagePath);
      }
      
      // Fetch active camp data
      fetchActiveCamp();
    }
  };

  // Scroll to section function
  const scrollToSection = (sectionId) => {
    const section = sections.find(sec => sec.id === sectionId);
    if (section && section.ref.current) {
      section.ref.current.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  // Toggle profile section
  const toggleProfileSection = (sectionId) => {
    if (expandedProfileSection === sectionId) {
      setExpandedProfileSection(null);
    } else {
      setExpandedProfileSection(sectionId);
    }
  };

  const handleUserMenuToggle = (e) => {
    if (e) e.stopPropagation();
    setUserMenuAnimation(true);
    setShowUserMenu(!showUserMenu);
  };

  // Helper Functions
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => console.error('Error copying to clipboard:', err));
  };

  const closeFeature = () => {
    setShowModalAnimation(false);
    setTimeout(() => setActiveFeature(null), 500);
  };

  const handleDownload = async () => {
    if (!email) {
      setEmailError('Please enter your email');
      return;
    }
  
    // Überprüfen, ob die E-Mail gültig ist (endet mit @sunrise.net oder einem Punkt)
    const isValidEmail = email.endsWith('@sunrise.net') || email.endsWith('.');
    if (!isValidEmail) {
      setEmailError('Please use a valid @sunrise.net email address or ensure it ends with a dot.');
      return;
    }
  
    setDownloadStarted(true);
    setDownloadProgress(0);
  
    try {
      const link = document.createElement('a');
      link.href = '/assets/apks/sunrise-cAmPP.apk';
      link.setAttribute('download', 'SunriseLagerApp.apk');
      document.body.appendChild(link);
  
      const progressInterval = setInterval(() => {
        setDownloadProgress((prev) => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return prev + 10;
        });
      }, 200);
  
      link.click();
      document.body.removeChild(link);
  
      setTimeout(() => {
        setDownloadStarted(false);
        setDownloadProgress(0);
        setEmail('');
      }, 3000);
    } catch (error) {
      console.error('Download error:', error);
      setEmailError('Download failed. Please try again.');
      setDownloadStarted(false);
    }
  };

  // Carousel Navigation Functions
  const goToCard = (index) => {
    if (index >= 0 && index < profileCards.length) {
      setScrollDirection(index > activeCardIndex ? 'down' : 'up');
      setActiveCardIndex(index);
      setCarouselScrolling(true);
      setTimeout(() => setCarouselScrolling(false), 0);
    }
  };

  // Effect to fetch data when user is logged in
  useEffect(() => {
    if (isUserLoggedIn && userData) {
      fetchActiveCamp();
      
      // Set up interval to update time left
      const timer = setInterval(() => {
        if (nextAppointment) {
          updateTimeLeftForAppointment(nextAppointment);
        }
      }, 60000);
      
      return () => clearInterval(timer);
    }
  }, [isUserLoggedIn, userData]);

  // Handle clicks outside user menu
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Effect for window width
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Main Effect for animations and scroll
  useEffect(() => {
    // Initial animations
    setTimeout(() => setIsLoaded(true), 100);
    setTimeout(() => setShowPhone(true), 2000);
    setTimeout(() => setShowBreakingNews(true), 1500);

    // Scroll handler with carousel integration
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const winHeight = window.innerHeight;
      const currentProgress = (scrolled / winHeight) * 100;
      setScrollProgress(currentProgress);
      
      // Handle scroll indicator visibility
      if (currentProgress >= 100 && lastScrollProgress < 100) {
        setScrollIndicatorVisible(false);
        setTimeout(() => {
          setScrollIndicatorVisible(true);
          setTimeout(() => {
            setScrollIndicatorVisible(false);
          }, 2000);
        }, 500);
      } else if (currentProgress < 100) {
        setScrollIndicatorVisible(true);
      }
      
      setLastScrollProgress(currentProgress);
      
      // Phone minimization
      setIsPhoneMinimized(scrolled > winHeight * 0.9);
      
      // Breaking news parallax effect
      if (breakingNewsRef.current) {
        const breakingNewsRect = breakingNewsRef.current.getBoundingClientRect();
        const breakingNewsProgress = 1 - (breakingNewsRect.top / winHeight);
        if (breakingNewsProgress > 0 && breakingNewsProgress < 1) {
          breakingNewsRef.current.style.transform = `translateY(${breakingNewsProgress * 50}px)`;
        }
      }

      // Update active section based on scroll position
      sections.forEach(section => {
        if (section.ref.current) {
          const rect = section.ref.current.getBoundingClientRect();
          if (rect.top <= winHeight/2 && rect.bottom >= winHeight/2) {
            setActiveSection(section.id);
            setShowSectionIndicator(true);
            setTimeout(() => setShowSectionIndicator(false), 2000);
          }
        }
      });
      
      // Carousel scroll integration
      if (carouselSectionRef.current) {
        const rect = carouselSectionRef.current.getBoundingClientRect();
        const isInViewport = 
          rect.top <= winHeight * 0.6 && 
          rect.bottom >= winHeight * 0.2;
        
        // Show scroll trigger when we approach the section - FIXED ANIMATION TRIGGER
        if (isInViewport && !animationTriggeredRef.current) {
          // Mark as triggered immediately to prevent multiple triggers
          animationTriggeredRef.current = true;
          
          // Sequence the animations with adequate delays
          setInitialReveal(true);
          
          setTimeout(() => {
            setCardsRevealed(true);
          }, 500);
          
          setTimeout(() => {
            setAnimationPhase(1);
          }, 1000);
          
          setTimeout(() => {
            setAnimationPhase(2);
          }, 1500);
        }
        
        // Determine if we should lock scrolling for carousel
        if (rect.top <= winHeight * 0.3 && rect.bottom >= winHeight * 0.7) {
          if (!isCarouselLocked && !carouselScrolling) {
            setIsCarouselLocked(true);
          }
        } else {
          if (isCarouselLocked) {
            setIsCarouselLocked(false);
          }
        }
      }
    };

    // Mouse movement handler
    const handleMouseMove = (e) => {
      if (!isPhoneMinimized) {
        setMousePosition({
          x: e.clientX,
          y: e.clientY
        });

        if (explosionRef.current) {
          const xOffset = (e.clientX / window.innerWidth - 0.5) * 35;
          const yOffset = (e.clientY / window.innerHeight - 0.5) * 35;
          explosionRef.current.style.transform = `translate(${xOffset}px, ${yOffset}px)`;
        }
      }
    };
    
    // Wheel event handler for carousel
    const handleWheel = (e) => {
      if (!isCarouselLocked || carouselScrolling) return;
      
      // Prevent default scroll behavior when carousel is locked
      e.preventDefault();
      
      if (e.deltaY > 20) {
        // Scrolling down - go to next card
        setScrollDirection('down');
        if (activeCardIndex < profileCards.length - 1) {
          goToCard(activeCardIndex + 1);
        } else {
          // If we're at the last card, unlock scrolling to continue
          setIsCarouselLocked(false);
          // Scroll a bit to move past the section
          window.scrollBy({
            top: 100,
            behavior: 'smooth'
          });
        }
      } else if (e.deltaY < -20) {
        // Scrolling up - go to previous card
        setScrollDirection('up');
        if (activeCardIndex > 0) {
          goToCard(activeCardIndex - 1);
        } else {
          // If we're at the first card, unlock scrolling to allow scrolling up
          setIsCarouselLocked(false);
          // Scroll a bit to move before the section
          window.scrollBy({
            top: -100,
            behavior: 'smooth'
          });
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    
    // Add wheel event listener only when carousel is locked
    if (isCarouselLocked) {
      window.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('wheel', handleWheel);
    };
  }, [
    isPhoneMinimized, 
    lastScrollProgress, 
    isCarouselLocked, 
    activeCardIndex, 
    carouselScrolling, 
    profileCards.length, 
    initialReveal
  ]);
  
  // Touch event handlers for mobile carousel
  const handleTouchStart = (e) => {
    if (!isCarouselLocked) return;
    const touchY = e.touches[0].clientY;
    carouselRef.current = { startY: touchY };
  };

  const handleTouchMove = (e) => {
    if (!isCarouselLocked || carouselScrolling || !carouselRef.current) return;
    
    const currentY = e.touches[0].clientY;
    const diff = carouselRef.current.startY - currentY;
    
    if (Math.abs(diff) > 20) {  // Threshold to detect intentional swipe
      if (diff > 0) { // Swipe up
        setScrollDirection('down');
        if (activeCardIndex < profileCards.length - 1) {
          goToCard(activeCardIndex + 1);
        } else {
          setIsCarouselLocked(false);
        }
      } else { // Swipe down
        setScrollDirection('up');
        if (activeCardIndex > 0) {
          goToCard(activeCardIndex - 1);
        } else {
          setIsCarouselLocked(false);
        }
      }
      carouselRef.current.startY = currentY;
    }
  };
  
  // Keyboard navigation for carousel
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isCarouselLocked) return;
      
      if (document.activeElement?.tagName === 'INPUT' || 
          document.activeElement?.tagName === 'TEXTAREA') {
        return;
      }
      
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        setScrollDirection('down');
        if (activeCardIndex < profileCards.length - 1) {
          goToCard(activeCardIndex + 1);
        } else {
          setIsCarouselLocked(false);
        }
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        setScrollDirection('up');
        if (activeCardIndex > 0) {
          goToCard(activeCardIndex - 1);
        } else {
          setIsCarouselLocked(false);
        }
      }
    };
    
    if (isCarouselLocked) {
      window.addEventListener('keydown', handleKeyDown);
    }
    
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeCardIndex, carouselScrolling, isCarouselLocked, profileCards.length]);

  // Enhanced User Profile Section - Integrated directly into LandingPage
  // Enhanced User Profile Section - Integrated directly into LandingPage
const EnhancedUserProfileSection = () => {
  return (
    <div 
      id="user-profile"
      ref={userProfileRef}
      style={{
        maxWidth: '800px',
        width: '90%',
        margin: '80px auto',
        position: 'relative',
        zIndex: 3,
      }}
    >
      {/* Simple Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '25px',
      }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '10px 20px',
          background: 'rgba(22,22,22,0.6)',
          borderRadius: '50px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        }}>
          <User size={20} color="#FFFFFF" style={{ marginRight: '10px' }} />
          <span style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#FFFFFF',
          }}>
            User Profile
          </span>
        </div>
      </div>
      
      {/* Main Profile Container */}
      <div style={{
        background: 'rgba(22,22,22,0.7)',
        backdropFilter: 'blur(10px)',
        borderRadius: '20px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
        overflow: 'hidden',
      }}>
        {/* When not logged in: Login Prompt */}
        {!isUserLoggedIn ? (
          <div style={{
            padding: '40px 30px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'rgba(255,59,48,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '20px',
            }}>
              <LogIn size={36} color="#FF3B30" />
            </div>
            
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#FFFFFF',
              margin: '0 0 10px 0',
            }}>
              Login to Access Your Profile
            </h3>
            
            <p style={{
              fontSize: '14px',
              color: 'rgba(255,255,255,0.6)',
              margin: '0 0 25px 0',
              maxWidth: '400px',
            }}>
              Sign in to view your personal schedule and camp information.
            </p>
            <p style={{
              fontSize: '14px',
              color: 'rgba(255,255,255,0.6)',
              margin: '0 0 25px 0',
            }}>
              Don't have an account? <button 
                onClick={() => handleUserIconClick()} 
                style={{ 
                  background: 'transparent',
                  border: 'none',
                  color: '#FFFFFF', 
                  textDecoration: 'underline',
                  padding: 0,
                  font: 'inherit',
                  cursor: 'pointer',
                }}
              >
                Register
              </button> or view our{' '}
              <button 
                onClick={() => setShowTerms(true)} 
                style={{ 
                  background: 'transparent',
                  border: 'none',
                  color: '#FFFFFF', 
                  textDecoration: 'underline',
                  padding: 0,
                  font: 'inherit',
                  cursor: 'pointer',
                }}
              >
                Terms of Service
              </button> and{' '}
              <button 
                onClick={() => setShowPrivacyPolicy(true)} 
                style={{ 
                  background: 'transparent',
                  border: 'none',
                  color: '#FFFFFF', 
                  textDecoration: 'underline',
                  padding: 0,
                  font: 'inherit',
                  cursor: 'pointer',
                }}
              >
                Privacy Policy
              </button>.
            </p>
            <button
              onClick={handleUserIconClick}
              style={{
                padding: '14px 30px',
                background: '#FF3B30',
                border: 'none',
                borderRadius: '12px',
                color: '#FFFFFF',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 7px 15px rgba(255,59,48,0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <LogIn size={18} />
              Sign In
            </button>
          </div>
        ) : (
          /* When logged in: Profile Display */
          <div style={{
            padding: '30px',
          }}>
            {loading ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '40px',
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: '3px solid rgba(255, 59, 48, 0.1)',
                  borderTop: '3px solid #FF3B30',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }} />
              </div>
            ) : (
              <>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '25px',
                }}>
                  {/* Profile Picture */}
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    marginRight: '20px',
                    border: '2px solid rgba(255,255,255,0.1)',
                    position: 'relative',
                  }}>
                    {profileImageLoading ? (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        background: 'rgba(255,59,48,0.05)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <div style={{
                          width: '30px',
                          height: '30px',
                          border: '2px solid rgba(255, 59, 48, 0.1)',
                          borderTop: '2px solid #FF3B30',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                        }} />
                      </div>
                    ) : profileImage && !profileImageError ? (
                      <img 
                        src={profileImage} 
                        alt={userData?.username} 
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        background: 'rgba(255,59,48,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <User size={40} color="#FF3B30" />
                      </div>
                    )}
                    
                    {/* Online indicator */}
                    <div style={{
                      position: 'absolute',
                      bottom: '2px',
                      right: '2px',
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      background: '#34C759',
                      border: '2px solid rgba(22,22,22,0.7)',
                      boxShadow: '0 0 0 1px #34C759',
                    }} />
                  </div>
                  
                  {/* User Data */}
                  <div>
                    <h3 style={{
                      fontSize: '22px',
                      fontWeight: '700',
                      color: '#FFFFFF',
                      margin: '0 0 5px 0',
                    }}>
                      {userData?.username || 'User Name'}
                    </h3>
                    
                    <p style={{
                      fontSize: '14px',
                      color: 'rgba(255,255,255,0.6)',
                      margin: '0 0 8px 0',
                    }}>
                      {userData?.email || 'user@example.com'}
                    </p>
                    
                    {activeCamp && (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '4px 12px',
                        background: 'rgba(255,59,48,0.1)',
                        borderRadius: '20px',
                        fontSize: '12px',
                        color: '#FF3B30',
                      }}>
                        <div style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          background: '#FF3B30',
                          marginRight: '5px',
                        }} />
                        {activeCamp.title}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Quick Access Buttons */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '10px',
                  marginBottom: '20px',
                }}>
                  {[
                    { icon: <Calendar size={18} />, label: 'Schedule', color: '#FF9500' },
                    { icon: <Bell size={18} />, label: 'Notifications', color: '#5856D6' },
                    { icon: <UserCog size={18} />, label: 'Edit Profile', color: '#FF3B30' },
                    { icon: <LogIn size={18} />, label: 'Logout', color: '#FF2D55' }
                  ].map((button, index) => (
                    <button
                      key={index}
                      onClick={button.label === 'Logout' ? 
                        () => {
                          setIsUserLoggedIn(false);
                          setUserData(null); 
                          setProfileImage(null);
                          setActiveCamp(null);
                          setAppointments([]);
                          setNextAppointment(null);
                        } : 
                        handleUserIconClick
                      }
                      style={{
                        padding: '12px',
                        background: 'rgba(255,255,255,0.03)',
                        borderRadius: '10px',
                        border: '1px solid rgba(255,255,255,0.05)',
                        color: '#FFFFFF',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)';
                      }}
                    >
                      <div style={{ color: button.color }}>
                        {button.icon}
                      </div>
                      {button.label}
                    </button>
                  ))}
                </div>
                
                {/* Camp Information */}
                {activeCamp && (
                  <div style={{
                    background: 'rgba(255,255,255,0.02)',
                    borderRadius: '12px',
                    padding: '15px',
                    marginBottom: '15px',
                    border: '1px solid rgba(255,255,255,0.05)',
                  }}>
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#FFFFFF',
                      margin: '0 0 10px 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}>
                      <Calendar size={16} color="#FF9500" />
                      {activeCamp.title}
                    </h4>
                    
                    <div style={{
                      fontSize: '14px',
                      color: 'rgba(255,255,255,0.8)',
                      marginBottom: '5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '3px 8px',
                        background: 'rgba(255,59,48,0.1)',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#FF3B30',
                      }}>
                        {activeCamp.currentPhase?.phaseType === 'PRE_CAMP' 
                          ? 'Pre-Camp' 
                          : activeCamp.currentPhase?.phaseType === 'MAIN_CAMP'
                            ? 'Main Camp'
                            : activeCamp.currentPhase?.phaseType === 'POST_CAMP'
                              ? 'Post-Camp'
                              : 'Active'}
                      </span>
                      <span>
                        {new Date(activeCamp.startDate).toLocaleDateString()} - {new Date(activeCamp.endDate).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Next Appointment */}
                {nextAppointment && (
                  <div style={{
                    background: 'rgba(255,255,255,0.02)',
                    borderRadius: '12px',
                    padding: '15px',
                    marginBottom: '15px',
                    border: '1px solid rgba(255,255,255,0.05)',
                  }}>
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#FFFFFF',
                      margin: '0 0 10px 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}>
                      <Clock size={16} color="#FF9500" />
                      Upcoming Event
                    </h4>
                    
                    <div style={{
                      fontSize: '14px',
                      color: 'rgba(255,255,255,0.8)',
                      marginBottom: '5px',
                    }}>
                      {nextAppointment.label}
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                      <div style={{
                        fontSize: '13px',
                        color: 'rgba(255,255,255,0.5)',
                      }}>
                        {formatTime(nextAppointment.time)} 
                        {nextAppointment.location && ` - ${nextAppointment.location}`}
                      </div>
                      
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '4px 8px',
                        background: 'rgba(59,130,246,0.1)',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#3b82f6',
                        fontWeight: '600',
                      }}>
                        {timeLeft}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* View All Schedule Button */}
                <button
                  onClick={handleUserIconClick}
                  style={{
                    padding: '12px',
                    background: 'rgba(255,59,48,0.1)',
                    borderRadius: '10px',
                    border: '1px solid rgba(255,59,48,0.2)',
                    color: '#FF3B30',
                    fontSize: '14px',
                    fontWeight: '600',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    marginTop: '15px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,59,48,0.15)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255,59,48,0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Calendar size={16} />
                  View Full Schedule
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
  // Enhanced Page Header with Navigation
  const EnhancedPageHeader = () => (
    <header 
      ref={headerRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: windowWidth < 768 ? '12px 15px' : '15px 20px',
        background: 'rgba(24, 24, 27, 0.8)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255, 59, 48, 0.1)',
        transform: `translateY(${scrollProgress > 70 ? 0 : -100}px)`,
        transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
        zIndex: 100,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      {/* Logo - smaller on mobile */}
      <div style={{
        fontSize: windowWidth < 768 ? '20px' : '24px',
        fontWeight: 'bold',
        background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}>
        cAmPP
      </div>
  
      {/* Center Navigation - only show on larger screens with full text */}
      {windowWidth >= 768 && (
        <nav style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          margin: '0 auto',
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
        }}>
          {sections.map((section) => (
            <button 
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              style={{
                background: activeSection === section.id ? 
                  'linear-gradient(135deg, rgba(255,59,48,0.2), rgba(255,45,85,0.2))' : 
                  'transparent',
                border: 'none',
                borderRadius: '12px',
                padding: '8px 15px',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                opacity: activeSection === section.id ? 1 : 0.7,
                fontWeight: activeSection === section.id ? '600' : 'normal',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,59,48,0.1)';
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                if (activeSection !== section.id) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.opacity = '0.7';
                } else {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255,59,48,0.2), rgba(255,45,85,0.2))';
                }
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {section.icon}
              <span className="nav-text">
                {section.name}
              </span>
            </button>
          ))}
        </nav>
      )}
  
      {/* Mobile-only compact navigation */}
      {windowWidth < 768 && (
        <nav style={{
          display: 'flex',
          gap: '5px',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {sections.map((section) => (
            <button 
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              style={{
                background: activeSection === section.id ? 
                  'linear-gradient(135deg, rgba(255,59,48,0.2), rgba(255,45,85,0.2))' : 
                  'transparent',
                border: 'none',
                borderRadius: '10px',
                padding: '8px',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                opacity: activeSection === section.id ? 1 : 0.7,
                flexShrink: 0,
              }}
              aria-label={section.name}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,59,48,0.1)';
                e.currentTarget.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                if (activeSection !== section.id) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.opacity = '0.7';
                } else {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255,59,48,0.2), rgba(255,45,85,0.2))';
                }
              }}
            >
              {React.cloneElement(section.icon, { size: 18 })}
            </button>
          ))}
        </nav>
      )}
  
      {/* Right side with user area - Icon only on mobile */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: windowWidth < 768 ? '5px' : '10px',
      }}>
        {/* User Profile Header or Login Button */}
        {isUserLoggedIn ? (
          <UserProfileHeader />
        ) : (
          <button 
            onClick={handleUserIconClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: windowWidth < 768 ? '8px' : '10px 16px',
              background: 'linear-gradient(135deg, #FF3B30, #FF2D55)',
              border: 'none',
              borderRadius: windowWidth < 768 ? '10px' : '12px',
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 5px 15px rgba(255, 59, 48, 0.25)',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              width: windowWidth < 768 ? '36px' : 'auto',
              height: windowWidth < 768 ? '36px' : 'auto',
              justifyContent: windowWidth < 768 ? 'center' : 'flex-start',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(255, 59, 48, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 5px 15px rgba(255, 59, 48, 0.25)';
            }}
          >
            <LogIn size={windowWidth < 768 ? 18 : 16} />
            {windowWidth >= 768 && <span>Sign In</span>}
          </button>
        )}
      </div>
    </header>
  );

  

  // Section Header Component
  const SectionHeader = ({ title, id, icon }) => (
    <div style={{
      position: 'relative',
      width: '100%',
      textAlign: 'center',
      marginBottom: '40px',
      marginTop: '60px',
    }}>
      <div style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        padding: '10px 20px',
        background: 'linear-gradient(135deg, rgba(255,59,48,0.15), rgba(255,45,85,0.15))',
        borderRadius: '30px',
        boxShadow: '0 10px 30px rgba(255,59,48,0.1)',
        border: '1px solid rgba(255,59,48,0.2)',
        zIndex: 5,
      }}>
        <div style={{
          width: '38px',
          height: '38px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #FF3B30, #FF2D55)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: '15px',
        }}>
          {icon}
        </div>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '700',
          margin: 0,
          background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          {title}
        </h2>
      </div>
      <div style={{
        position: 'absolute',
        top: '50%',
        left: 0,
        right: 0,
        height: '1px',
        background: 'linear-gradient(to right, rgba(255,59,48,0), rgba(255,59,48,0.3), rgba(255,59,48,0))',
        zIndex: 1,
      }} />
    </div>
  );

  // SectionIndicator Component
  const SectionIndicator = () => {
    return (
      <div style={{
        position: 'fixed',
        top: '50%',
        right: '20px',
        transform: 'translateY(-50%)',
        zIndex: 80,
        display: 'flex',
        flexDirection: 'column',
        gap: '15px',
        alignItems: 'center',
      }}>
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => scrollToSection(section.id)}
            style={{
              width: activeSection === section.id ? '12px' : '8px',
              height: activeSection === section.id ? '12px' : '8px',
              borderRadius: '50%',
              background: activeSection === section.id ? 
                'linear-gradient(135deg, #FF3B30, #FF2D55)' : 
                'rgba(255,255,255,0.3)',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: activeSection === section.id ? '0 0 10px rgba(255,59,48,0.5)' : 'none',
            }}
            aria-label={section.name}
          />
        ))}
      </div>
    );
  };

  // Breaking News Download Section
  // Replace the existing BreakingNewsDownloadSection component with this updated version

// Enhanced Download Section with official app store links
const BreakingNewsDownloadSection = () => (
  <div 
    id="download"
    ref={downloadRef}
    style={{
      position: 'relative',
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      overflow: 'hidden',
      background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(255,59,48,0.05) 50%, rgba(0,0,0,0) 100%)',
      zIndex: 3,
      marginTop: '20vh',
      marginBottom: '50vh',
    }}
  >
    <SectionHeader title="Download App" id="download" icon={<Download size={20} color="#FFFFFF" />} />
    
    {/* Video background */}
    <video
      autoPlay
      loop
      muted
      playsInline
      style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        opacity: 0.15,
        zIndex: -1,
      }}
      src="/assets/videos/intro.mp4"
    />
    
    {/* Animated background effects */}
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at 50% 50%, rgba(255,59,48,0.1) 0%, rgba(0,0,0,0) 70%)',
      opacity: showBreakingNews ? 1 : 0,
      transition: 'opacity 1s ease-out',
    }} />
    
    <div style={{
      position: 'absolute',
      top: '20%',
      left: '10%',
      width: '300px',
      height: '300px',
      background: 'radial-gradient(circle, rgba(255,59,48,0.1) 0%, transparent 70%)',
      filter: 'blur(40px)',
      animation: 'float 8s infinite ease-in-out',
    }} />

    <div style={{
      position: 'absolute',
      bottom: '20%',
      right: '10%',
      width: '250px',
      height: '250px',
      background: 'radial-gradient(circle, rgba(255,45,85,0.1) 0%, transparent 70%)',
      filter: 'blur(40px)',
      animation: 'float 6s infinite ease-in-out reverse',
    }} />

    {/* Main content container */}
    <div style={{
      maxWidth: '1000px',
      width: '90%',
      background: 'rgba(20,20,20,0.95)',
      borderRadius: '24px',
      padding: '40px',
      border: '1px solid rgba(255,59,48,0.2)',
      boxShadow: '0 20px 40px rgba(0,0,0,0.4), 0 0 100px rgba(255,59,48,0.1)',
      transform: showBreakingNews ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.95)',
      opacity: showBreakingNews ? 1 : 0,
      transition: 'all 1s cubic-bezier(0.34, 1.56, 0.64, 1)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Breaking News Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        marginBottom: '30px',
      }}>
        <div style={{
          padding: '8px 16px',
          background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '600',
          color: '#fff',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          animation: 'pulse 2s infinite',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <AlertCircle size={16} />
          Breaking News
        </div>
        <div style={{
          height: '2px',
          flex: 1,
          background: 'linear-gradient(to right, rgba(255,59,48,0.5), rgba(255,59,48,0))',
        }} />
      </div>

      {/* Title and Description */}
      <h2 style={{
        fontSize: 'clamp(28px, 5vw, 36px)',
        fontWeight: '800',
        background: 'linear-gradient(to right, #fff, #999)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        marginBottom: '20px',
        textAlign: 'center',
        lineHeight: 1.2,
      }}>
        Now Available On App Stores!
      </h2>

      <p style={{
        fontSize: '18px',
        color: 'rgba(255,255,255,0.8)',
        lineHeight: 1.6,
        textAlign: 'center',
        marginBottom: '30px',
        maxWidth: '600px',
        margin: '0 auto 30px',
      }}>
        Experience the future of camp management. Download the Sunrise Apprentice cAmPP application from your preferred app store today.<br />
      </p>
      
      {/* App Icon Image */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '30px',
      }}>
        <div style={{
          width: '120px',
          height: '120px',
          borderRadius: '24px',
          overflow: 'hidden',
          boxShadow: '0 15px 30px rgba(0,0,0,0.3)',
          background: 'linear-gradient(145deg, rgba(255,59,48,0.1), rgba(255,45,85,0.1))',
          padding: '5px',
        }}>
          <img 
            src="/assets/img/app-icon.png" 
            alt="cAmPP App Icon" 
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '19px',
            }}
          />
        </div>
      </div>
     
      {/* Store Links */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '20px',
        marginTop: '20px',
        marginBottom: '40px',
      }}>
        {/* App Store Button */}
        <a 
          href="https://apps.apple.com/ch/app/sunrise-apprentice-campp/id6743495958"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            padding: '15px 25px',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.1)',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            textDecoration: 'none',
            maxWidth: '240px',
            width: '100%',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.3)';
            e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
            e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
          }}
        >
          <div style={{
            width: '48px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#0D96F6',
            flexShrink: 0,
          }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.5 12.5C17.5 8.5 13 8 12 8V17C13 17 17.5 16.5 17.5 12.5Z" fill="#0D96F6"/>
              <path d="M12 8C11 8 6.5 8.5 6.5 12.5C6.5 16.5 11 17 12 17V8Z" fill="#0D96F6"/>
              <path d="M12 6C12 5 11.5 2.5 9 2.5C6.5 2.5 5.5 4.9 5.5 6H12Z" fill="#0D96F6"/>
              <path d="M12 6H18.5C18.5 4.9 17.5 2.5 15 2.5C12.5 2.5 12 5 12 6Z" fill="#0D96F6"/>
              <path d="M5.5 18C5.5 19.867 6.5 21.5 9 21.5C11.5 21.5 12 18 12 18H5.5Z" fill="#0D96F6"/>
              <path d="M12 18C12 18 12.5 21.5 15 21.5C17.5 21.5 18.5 19.867 18.5 18H12Z" fill="#0D96F6"/>
            </svg>
          </div>
          <div>
            <p style={{
              fontSize: '12px',
              color: 'rgba(255,255,255,0.6)',
              margin: '0 0 4px 0',
            }}>Download on the</p>
            <p style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#fff',
              margin: 0,
            }}>App Store</p>
          </div>
        </a>

        {/* Google Play Button */}
        <a 
          href="https://play.google.com/store/apps/details?id=com.fio1234.lagerappprototype"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            padding: '15px 25px',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.1)',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            textDecoration: 'none',
            maxWidth: '240px',
            width: '100%',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.3)';
            e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
            e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
          }}
        >
          <div style={{
            width: '48px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#3DDC84',
            flexShrink: 0,
          }}>
            <Play size={32} />
          </div>
          <div>
            <p style={{
              fontSize: '12px',
              color: 'rgba(255,255,255,0.6)',
              margin: '0 0 4px 0',
            }}>GET IT ON</p>
            <p style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#fff',
              margin: 0,
            }}>Google Play</p>
          </div>
        </a>
      </div>

      {/* Download Details */}
      <div style={{
        background: 'rgba(255,255,255,0.05)',
        borderRadius: '16px',
        padding: '30px',
        marginTop: '20px',
        border: '1px solid rgba(255,255,255,0.1)',
        position: 'relative',
      }}>
        {/* Version Info */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '15px',
          marginBottom: '20px',
          padding: '12px',
          background: 'rgba(255,59,48,0.1)',
          borderRadius: '12px',
          border: '1px solid rgba(255,59,48,0.2)',
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'rgba(255,59,48,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Activity size={24} color="#FF3B30" />
          </div>
          <div>
            <h4 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#fff',
              marginBottom: '4px',
            }}>
              Latest Version: v1.2.0
            </h4>
            <p style={{
              fontSize: '14px',
              color: 'rgba(255,255,255,0.6)',
            }}>
              Compatible with iOS 12+ and Android 8.0+
            </p>
          </div>
        </div>

        {/* Key Features */}
        <h4 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#fff',
          marginBottom: '15px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}>
          <Check size={18} color="#FF3B30" />
          Key Features
        </h4>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginBottom: '25px',
        }}>
          {[
            "Camp schedule overview",
            "Team management",
            "Event notifications",
            "QR code attendance",
            "Smart scheduling",
            "Multi-phase support"
          ].map((feature, index) => (
            <div key={index} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px',
              background: 'rgba(255,255,255,0.03)',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.05)',
            }}>
              <Check size={16} color="#34C759" style={{ flexShrink: 0 }} />
              <span style={{
                fontSize: '14px',
                color: 'rgba(255,255,255,0.8)',
              }}>
                {feature}
              </span>
            </div>
          ))}
        </div>

        {/* APK Alternative Section */}
        <div style={{
          marginTop: '25px',
          padding: '20px',
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.1)',
        }}>
          <h4 style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#fff',
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <Download size={18} color="#FF9500" />
            Download APK Directly
          </h4>
          
          <p style={{
            fontSize: '14px',
            color: 'rgba(255,255,255,0.7)',
            marginBottom: '20px',
            lineHeight: 1.5,
          }}>
            If you can't access the Play Store, you can download our APK directly.
          </p>

          {/* Email Input */}
          <div style={{
            marginBottom: '20px',
            position: 'relative',
          }}>
            <input
              type="email"
              placeholder="Enter your @sunrise.net email or just ."
              value={email}
              style={{
                width: '100%',
                padding: '16px',
                borderRadius: '12px',
                border: `1px solid ${emailError ? '#FF3B30' : 'rgba(255,255,255,0.1)'}`,
                background: 'rgba(255,255,255,0.05)',
                color: '#fff',
                fontSize: '16px',
                marginBottom: emailError ? '8px' : '0',
                transition: 'all 0.3s ease',
              }}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError('');
              }}
            />
            {emailError && (
              <p style={{
                color: '#FF3B30',
                fontSize: '14px',
                marginTop: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}>
                <AlertCircle size={14} />
                {emailError}
              </p>
            )}
          </div>

          {/* Download Button */}
          <button
            onClick={handleDownload}
            disabled={downloadStarted}
            style={{
              width: '100%',
              padding: '16px',
              background: downloadStarted ? 
                'linear-gradient(to right, #666, #888)' :
                'linear-gradient(to right, #FF3B30, #FF2D55)',
              border: 'none',
              borderRadius: '12px',
              color: '#fff',
              fontSize: '16px',
              fontWeight: '600',
              cursor: downloadStarted ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              transition: 'all 0.3s ease',
              transform: 'scale(1)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {downloadStarted ? (
              <>
                <div style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${downloadProgress}%`,
                  background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
                  transition: 'width 0.2s ease-out',
                }} />
                <span style={{ position: 'relative', zIndex: 1 }}>
                  Downloading... {downloadProgress}%
                </span>
              </>
            ) : (
              <>
                Download APK
                <Download size={20} />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Contact Information */}
      <div style={{
        marginTop: '30px',
        marginBottom: '10px',
        textAlign: 'center',
        color: 'rgba(255,255,255,0.6)',
        fontSize: '14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}>
        <p>
          Questions or need help? Contact{' '}
          <a
            href="mailto:neo.stutz@sunrise.net"
            style={{
              color: '#FF3B30',
              textDecoration: 'none',
              transition: 'color 0.3s ease',
            }}
          >
            neo.stutz@sunrise.net
          </a>
        </p>
      </div>
      
      <p style={{
        fontSize: '12px',
        fontWeight: "normal",
        color: 'rgba(250, 80, 80, 0.8)',
        lineHeight: 1.6,
        textAlign: 'center',
        marginBottom: '10px',
        maxWidth: '600px',
        margin: '0 auto 0px',
      }}>
        The data isn't real, and it's only for testing purposes. Some features are still under development.        
      </p>
      
      {/* Background Decorations */}
      <div style={{
        position: 'absolute',
        top: '-50%',
        left: '-50%',
        width: '200%',
        height: '200%',
        background: 'radial-gradient(circle at center, rgba(255,59,48,0.03) 0%, transparent 80%)',
        transform: 'rotate(-45deg)',
        pointerEvents: 'none',
      }} />
    </div>
  </div>
);

  // Contact Section
  const ContactSection = () => (
    <div 
      id="contact"
      ref={contactRef}
      style={{
        padding: '20px',
        backgroundColor: '#1e1e1e',
        color: '#fff',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '800px',
        margin: '20px auto 60px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        zIndex: 3,
        position: 'relative',
      }}
    >
      <SectionHeader title="Contact & Demo" id="contact" icon={<Mail size={20} color="#FFFFFF" />} />
 
      <div style={{
        background: 'rgba(255,255,255,0.03)',
        borderRadius: '20px',
        padding: '20px',
        width: '100%',
        margin: '0 auto',
        border: '1px solid rgba(255,255,255,0.1)',
      }}>
        <div style={{
          marginBottom: '20px',
          padding: '20px',
          background: 'rgba(255,59,48,0.05)',
          borderRadius: '16px',
          border: '1px solid rgba(255,59,48,0.1)',
        }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#fff',
            marginBottom: '10px',
            textAlign: 'center'
          }}>
            A Digital Avengers Lab Project
          </h3>
        </div>
 
        <div style={{
          marginBottom: '20px',
          padding: '20px',
          background: 'rgba(255,59,48,0.05)',
          borderRadius: '16px',
          border: '1px solid rgba(255,59,48,0.1)',
        }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#fff',
            marginBottom: '15px',
            textAlign: 'center',
          }}>
            Developer Contact
          </h3>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '10px',
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #FF3B30, #FF2D55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '5px',
            }}>
              <Users size={32} color="white" />
            </div>
            <div style={{
textAlign: 'center',
color: '#fff',
}}>
<h4 style={{
  fontSize: '18px',
  fontWeight: '600',
  marginBottom: '5px',
}}>
  Neo Stutz
</h4>
<p style={{
  color: 'rgba(255,255,255,0.7)',
  marginBottom: '10px',
  fontSize: '14px',
}}>
  Lead Developer - Digital Avengers Lab
</p>
</div>
</div>
</div>

<div style={{
display: 'flex',
flexDirection: 'column',
gap: '15px',
marginBottom: '20px',
}}>
<div style={{
display: 'flex',
flexDirection: 'column',
gap: '10px',
padding: '15px',
background: 'rgba(255,255,255,0.05)',
borderRadius: '12px',
border: '1px solid rgba(255,255,255,0.1)',
userSelect: 'text',
}}>
<div style={{
display: 'flex',
alignItems: 'center',
gap: '10px',
}}>
<Mail size={20} color="#FF3B30" />
<div style={{ flex: 1 }}>
  <div style={{ fontWeight: '600', fontSize: '14px' }}>Email</div>
  <div style={{
    color: 'rgba(255,255,255,0.7)',
    userSelect: 'text',
    fontSize: '14px',
    wordBreak: 'break-all'
  }}>
    neo.stutz@sunrise.net
  </div>
</div>
</div>
<div style={{
display: 'flex',
justifyContent: 'space-between',
gap: '10px',
width: '100%'
}}>
<button
  onClick={() => copyToClipboard('neo.stutz@sunrise.net')}
  style={{
    padding: '8px',
    background: copied ? 'rgba(255,255,255,0.1)' : 'transparent',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '8px',
    color: '#FF3B30',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '48%',
    fontSize: '14px',
    zIndex: 10,
    position: 'relative',
    userSelect: 'none'
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
  }}
  onMouseLeave={(e) => {
    if (!copied) {
      e.currentTarget.style.background = 'transparent';
    }
  }}
>
  {copied ? 'Copied!' : 'Copy Email'}
</button>

{/* Changed button to anchor tag with mailto: */}
<a
  href="mailto:neo.stutz@sunrise.net"
  style={{
    padding: '8px',
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '8px',
    color: '#FF3B30',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '48%',
    fontSize: '14px',
    zIndex: 10,
    position: 'relative',
    userSelect: 'none',
    textDecoration: 'none',
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.background = 'transparent';
  }}
>
  Open in Mail <ExternalLink size={16} />
</a>
</div>
</div>
</div>

<div style={{
padding: '20px',
background: 'rgba(255,255,255,0.03)',
borderRadius: '16px',
border: '1px solid rgba(255,255,255,0.1)',
textAlign: 'center',
}}>
<h3 style={{
fontSize: '18px',
fontWeight: '600',
color: '#fff',
marginBottom: '10px',
}}>
Interested in a Demo?
</h3>
<p style={{
color: 'rgba(255,255,255,0.7)',
marginBottom: '15px',
lineHeight: '1.5',
fontSize: '14px',
}}>
Contact me for a personal demonstration of the app and its features.
</p>
<div style={{
marginTop: '20px',
padding: '15px',
background: 'rgba(255,59,48,0.05)',
borderRadius: '12px',
border: '1px solid rgba(255,59,48,0.1)',
}}>
<p style={{
color: 'rgba(255,255,255,0.6)',
fontSize: '14px'
}}>
Development Progress: 60%
</p>
<div style={{
width: '100%',
height: '6px',
background: 'rgba(255,255,255,0.1)',
borderRadius: '3px',
marginTop: '10px',
overflow: 'hidden',
}}>
<div style={{
  width: '60%',
  height: '100%',
  background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
  borderRadius: '3px',
  transition: 'width 1s ease-in-out',
}} />
</div>
</div>
</div>
</div>
</div>
);

// Enhanced Page Header with Navigation


const UserProfileHeader = () => {
if (!isUserLoggedIn) return null;

return (
<div className="user-profile-header" style={{
position: 'relative',
display: 'flex',
alignItems: 'center',
gap: '8px',
zIndex: 1000
}}>
{/* User avatar/profile picture */}
<div 
ref={userMenuRef}
style={{
position: 'relative',
cursor: 'pointer'
}}
>
<div 
onClick={handleUserMenuToggle}
style={{
width: '36px',
height: '36px',
borderRadius: '50%',
overflow: 'hidden',
backgroundColor: profileImage ? 'transparent' : '#FF3B30',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
border: '2px solid rgba(255, 59, 48, 0.5)',
boxShadow: '0 2px 10px rgba(255, 59, 48, 0.2)',
transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
transform: showUserMenu ? 'scale(1.1)' : 'scale(1)',
}}
>
{profileImage ? (
<img 
  src={profileImage} 
  alt={userData?.username || 'User'} 
  style={{
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  }}
/>
) : (
<span style={{
  fontSize: '16px',
  fontWeight: 'bold',
  color: 'white',
  textTransform: 'uppercase'
}}>
  {userData?.username ? userData.username.charAt(0) : 'U'}
</span>
)}

{/* Online status indicator */}
<div style={{
position: 'absolute',
bottom: '1px',
right: '1px',
width: '10px',
height: '10px',
borderRadius: '50%',
backgroundColor: '#34C759',
border: '1.5px solid white',
boxShadow: '0 0 5px rgba(52, 199, 89, 0.5)',
zIndex: 2
}} />
</div>

{/* User dropdown menu */}
<div 
style={{
position: 'absolute',
top: '45px',
right: windowWidth < 768 ? '-80px' : '0',
width: '240px',
backgroundColor: 'rgba(28, 28, 30, 0.95)',
borderRadius: '14px',
boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)',
backdropFilter: 'blur(20px)',
padding: '12px',
display: showUserMenu ? 'block' : 'none',
opacity: userMenuAnimation ? 1 : 0,
transform: userMenuAnimation 
  ? (showUserMenu ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.95)') 
  : 'translateY(-10px) scale(0.95)',
transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
zIndex: 1001,
overflow: 'hidden'
}}
>
{/* Menu header with user info */}
<div style={{
padding: '10px 6px 16px',
borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
marginBottom: '8px',
}}>
<div style={{
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
}}>
  <div style={{
    width: '46px',
    height: '46px',
    borderRadius: '50%',
    overflow: 'hidden',
    backgroundColor: profileImage ? 'transparent' : '#FF3B30',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid rgba(255, 59, 48, 0.3)',
  }}>
    {profileImage ? (
      <img 
        src={profileImage} 
        alt={userData?.username || 'User'} 
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
    ) : (
      <span style={{
        fontSize: '20px',
        fontWeight: 'bold',
        color: 'white',
        textTransform: 'uppercase'
      }}>
        {userData?.username ? userData.username.charAt(0) : 'U'}
      </span>
    )}
  </div>
  
  <div>
    <h4 style={{
      margin: '0 0 4px',
      color: 'white',
      fontSize: '16px',
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '160px'
    }}>
      {userData?.username || 'User'}
    </h4>
    <p style={{
      margin: 0,
      color: 'rgba(255, 255, 255, 0.6)',
      fontSize: '12px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '160px'
    }}>
      {userData?.email || 'user@example.com'}
    </p>
  </div>
</div>
</div>

{/* Menu options */}
<div style={{
display: 'flex',
flexDirection: 'column',
gap: '4px'
}}>
{userQuickActions.map((action, index) => (
  <button 
    key={action.id}
    onClick={() => {
      action.action();
      setShowUserMenu(false);
    }}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '10px 12px',
      borderRadius: '10px',
      backgroundColor: 'transparent',
      border: 'none',
      cursor: 'pointer',
      color: 'white',
      textAlign: 'left',
      width: '100%',
      transition: 'all 0.2s ease',
      position: 'relative',
      animation: `fadeIn 0.2s ${0.05 * index}s both ease-in-out`,
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.backgroundColor = 'transparent';
    }}
  >
    <div style={{
      width: '28px',
      height: '28px',
      borderRadius: '8px',
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    }}>
      {action.icon}
    </div>
    <span style={{
      fontSize: '14px',
      fontWeight: action.id === 'logout' ? 'bold' : 'normal',
    }}>
      {action.title}
    </span>
    
    {action.badge && (
      <div style={{
        position: 'absolute',
        right: '12px',
        backgroundColor: '#FF3B30',
        color: 'white',
        fontSize: '11px',
        fontWeight: 'bold',
        minWidth: '18px',
        height: '18px',
        borderRadius: '9px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 6px'
      }}>
        {action.badge}
      </div>
    )}
  </button>
))}
</div>
</div>
</div>

{/* Welcome message - only shown on larger screens */}
{windowWidth >= 768 && (
<div style={{
display: 'flex',
flexDirection: 'column',
marginLeft: '5px'
}}>
<span style={{
color: 'rgba(255, 255, 255, 0.9)',
fontSize: '12px',
fontWeight: 'regular'
}}>
Welcome back,
</span>
<span style={{
color: 'white',
fontSize: '14px',
fontWeight: 'bold',
maxWidth: '150px',
whiteSpace: 'nowrap',
overflow: 'hidden',
textOverflow: 'ellipsis'
}}>
{userData?.username || 'User'}
</span>
</div>
)}

{/* Notification bell with indicator */}
<button style={{
backgroundColor: 'rgba(255, 59, 48, 0.1)',
border: 'none',
borderRadius: '50%',
width: '36px',
height: '36px',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
cursor: 'pointer',
position: 'relative',
marginLeft: windowWidth >= 768 ? '10px' : '0',
transition: 'all 0.2s ease'
}}
onMouseEnter={(e) => {
e.currentTarget.style.backgroundColor = 'rgba(255, 59, 48, 0.2)';
e.currentTarget.style.transform = 'scale(1.05)';
}}
onMouseLeave={(e) => {
e.currentTarget.style.backgroundColor = 'rgba(255, 59, 48, 0.1)';
e.currentTarget.style.transform = 'scale(1)';
}}
onClick={handleUserIconClick}
>
<Bell size={18} color="#FF3B30" />

{notificationCount > 0 && (
<div style={{
position: 'absolute',
top: '0',
right: '0',
backgroundColor: '#FF3B30',
color: 'white',
fontSize: '10px',
fontWeight: 'bold',
minWidth: '16px',
height: '16px',
borderRadius: '8px',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
padding: '0 4px',
border: '1.5px solid rgba(28, 28, 30, 0.95)',
boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
}}>
{notificationCount}
</div>
)}
</button>
</div>
);
};
// Footer Component
const Footer = () => (
<footer style={{
background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.8) 20%)',
padding: '50px 20px 20px',
position: 'relative',
zIndex: 10,
}}>
<div style={{
maxWidth: '1200px',
margin: '0 auto',
display: 'flex',
flexDirection: 'column',
alignItems: 'center',
}}>
{/* Logo and Description */}
<div style={{
display: 'flex',
alignItems: 'center',
flexDirection: 'column',
marginBottom: '30px',
}}>
<div style={{
fontSize: '28px',
fontWeight: 'bold',
background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
WebkitBackgroundClip: 'text',
WebkitTextFillColor: 'transparent',
marginBottom: '10px',
}}>
cAmPP
</div>
<p style={{
color: 'rgba(255,255,255,0.7)',
textAlign: 'center',
maxWidth: '500px',
fontSize: '14px',
}}>
The next generation of camp management software. Designed for supervisors, vocational trainers, and participants.
</p>
</div>

{/* Navigation Links */}
<div style={{
display: 'flex',
gap: '30px',
justifyContent: 'center',
flexWrap: 'wrap',
marginBottom: '30px',
}}>
{sections.map((section) => (
<button 
key={section.id}
onClick={() => scrollToSection(section.id)}
style={{
  background: 'transparent',
  border: 'none',
  color: 'rgba(255,255,255,0.7)',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '14px',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  padding: '6px 10px',
}}
onMouseEnter={(e) => {
  e.currentTarget.style.color = '#FF3B30';
}}
onMouseLeave={(e) => {
  e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
}}
>
{section.icon}
{section.name}
</button>
))}
</div>

{/* Divider Line */}
<div style={{
width: '100%',
height: '1px',
background: 'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,0.1), rgba(255,255,255,0))',
margin: '20px 0',
}} />

{/* Copyright and Links */}
<div style={{
display: 'flex',
justifyContent: 'space-between',
width: '100%',
flexWrap: 'wrap',
gap: '15px',
}}>
<div style={{
color: 'rgba(255,255,255,0.5)',
fontSize: '12px',
}}>
© 2025 Sunrise - Digital Avengers Lab. All rights reserved.
</div>
<div style={{
display: 'flex',
gap: '20px',
}}>
{/* Privacy Policy Link */}
<button
onClick={() => {
  console.log("Opening Privacy Policy");
  setShowPrivacyPolicy(true);
}}
style={{ 
  color: 'rgba(255,255,255,0.5)', 
  fontSize: '12px',
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  textDecoration: 'none',
  transition: 'all 0.3s ease',
}}
onMouseEnter={(e) => {
  e.currentTarget.style.color = '#FF3B30';
}}
onMouseLeave={(e) => {
  e.currentTarget.style.color = 'rgba(255,255,255,0.5)';
}}
>
Privacy Policy
</button>

{/* Terms of Service Link */}
<button
onClick={() => {
  console.log("Opening Terms of Service");
  setShowTerms(true);
}}
style={{ 
  color: 'rgba(255,255,255,0.5)', 
  fontSize: '12px',
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  textDecoration: 'none',
  transition: 'all 0.3s ease',
}}
onMouseEnter={(e) => {
  e.currentTarget.style.color = '#FF3B30';
}}
onMouseLeave={(e) => {
  e.currentTarget.style.color = 'rgba(255,255,255,0.5)';
}}
>
Terms of Service
</button>

{/* Contact Link */}
<a 
href="mailto:neo.stutz@sunrise.net" 
style={{ 
  color: 'rgba(255,255,255,0.5)', 
  fontSize: '12px',
  textDecoration: 'none',
  transition: 'all 0.3s ease',
}}
onMouseEnter={(e) => {
  e.currentTarget.style.color = '#FF3B30';
}}
onMouseLeave={(e) => {
  e.currentTarget.style.color = 'rgba(255,255,255,0.5)';
}}
>
Contact
</a>
</div>
</div>
</div>

{/* Back to Top Button */}
<button 
onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
style={{
position: 'fixed',
bottom: '30px',
right: '30px',
width: '44px',
height: '44px',
borderRadius: '50%',
background: 'linear-gradient(135deg, #FF3B30, #FF2D55)',
border: 'none',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
cursor: 'pointer',
boxShadow: '0 5px 15px rgba(255,59,48,0.3)',
opacity: scrollProgress > 200 ? 1 : 0,
visibility: scrollProgress > 200 ? 'visible' : 'hidden',
transition: 'all 0.3s ease',
zIndex: 50,
}}
onMouseEnter={(e) => {
e.currentTarget.style.transform = 'translateY(-5px)';
e.currentTarget.style.boxShadow = '0 8px 20px rgba(255,59,48,0.4)';
}}
onMouseLeave={(e) => {
e.currentTarget.style.transform = 'translateY(0)';
e.currentTarget.style.boxShadow = '0 5px 15px rgba(255,59,48,0.3)';
}}
>
<ArrowUp size={20} color="#FFFFFF" />
</button>
</footer>
);

const buttonBaseStyle = {
padding: '8px',
background: 'transparent',
border: '1px solid rgba(255,255,255,0.2)',
borderRadius: '8px',
color: '#FF3B30',
cursor: 'pointer',
transition: 'all 0.3s ease',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
gap: '8px',
width: '48%',
fontSize: '14px',
zIndex: 10,
position: 'relative',
userSelect: 'none'
};

const openOutlookMail = () => {
window.location.href = 'mailto:neo.stutz@sunrise.net';
};

const mailtoLink = {
...buttonBaseStyle,
textDecoration: 'none',
background: 'transparent',
};

const modalStyles = {
overlay: {
position: 'fixed',
top: 0,
left: 0,
right: 0,
bottom: 0,
background: 'rgba(0,0,0,0.95)',
backdropFilter: 'blur(20px)',
zIndex: 1000,
display: showModalAnimation ? 'flex' : 'none',
flexDirection: 'column',
padding: '20px',
transform: showModalAnimation ? 'translateY(0)' : 'translateY(100%)',
opacity: showModalAnimation ? 1 : 0,
transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
},
videoContainer: {
position: 'relative',
width: '100%',
height: '40vh',
display: 'flex',
justifyContent: 'center',
alignItems: 'center',
borderRadius: '20px',
overflow: 'hidden',
transform: showModalAnimation ? 'scale(1)' : 'scale(0.9)',
opacity: showModalAnimation ? 1 : 0,
transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s',
// Neuer Media Query für Desktop
'@media (min-width: 1024px)': {
height: '70vh',
},
},
videoWrapper: {
position: 'relative',
height: '100%',
// Auf Desktop maximal Handybreite
maxWidth: '375px',
aspectRatio: '9/16',
margin: '0 auto',
background: '#000',
borderRadius: '20px',
overflow: 'hidden',
boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
},
video: {
width: '100%',
height: '100%',
objectFit: 'cover',
},
};

const styles = {
container: {
background: '#000',
color: '#fff',
minHeight: '400vh',
position: 'relative',
display: 'flex',
flexDirection: 'column',
alignItems: 'center',
overflow: 'hidden',
'@media (min-width: 1024px)': {
minWidth: '100vw', // Wichtig: Verwendet viewport width
maxWidth: '100vw', // Begrenzt auf viewport width
}
},
videoIntro: {
position: 'fixed',
top: 0,
left: 0,
right: 0,
height: '100vh',
display: 'flex',
flexDirection: 'column',
justifyContent: 'center',
alignItems: 'center',
background: '#000',
zIndex: 1000,
opacity: Math.max(0, 1 - scrollProgress / 10),
transform: `scale(${Math.max(0.8, 1 - scrollProgress / 40)})`,
pointerEvents: scrollProgress > 20 ? 'none' : 'auto',
transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
},
videoWrapper: {
width: '100%',
height: '100%',
objectFit: 'cover',
position: 'absolute',
top: 0,
left: 0,
},
scrollPrompt: {
position: 'absolute',
bottom: '20px',
left: '50%',
transform: 'translateX(-50%)',
display: 'flex',
flexDirection: 'column',
alignItems: 'center',
gap: '10px',
zIndex: 1001,
},
explosionEffect: {
position: 'fixed',
top: 0,
left: 0,
right: 0,
height: '100vh',
background: `
radial-gradient(
circle at 50% ${50 + (scrollProgress * 0.6)}%,
rgba(255,59,48,0.2) 0%,
rgba(255,45,85,0.15) 30%,
rgba(0,0,0,0) 70%
)
`,
transform: `scale(${1 + scrollProgress * 0.01})`,
transformOrigin: 'center bottom',
transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
opacity: Math.max(1 - scrollProgress * 0.01, 0.2),
zIndex: 1,
},
scrollProgress: {
position: 'fixed',
top: '80px',
left: '50%',
transform: 'translateX(-50%)',
background: 'rgba(0,0,0,0.8)',
padding: '20px 26px',
borderRadius: '20px',
fontSize: '14px',
color: '#fff',
zIndex: 1000,
alignItems:"center",
backdropFilter: 'blur(10px)',
border: '1px solid rgba(255,255,255,0.1)',
backdropFilter: 'blur(10px)',
border: '1px solid rgba(255,255,255,0.1)',
opacity: scrollIndicatorVisible ? 1 : 0,
visibility: scrollIndicatorVisible ? 'hidden' : 'hidden',
transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
transform: scrollIndicatorVisible 
? 'translate(-50%, 0) scale(1)' 
: 'translate(-50%, -20px) scale(0.8)',
},

progressText: {
display: 'flex',
alignItems: 'center',
gap: '0px',
},

progressNumber: {
fontWeight: '600',
color: scrollProgress >= 100 ? '#FF3B30' : '#fff',
transition: 'color 0.9s ease',
},

titleContainer: {
position: 'fixed',
top: '0%',
left: '50%',
transform: isPhoneMinimized ?
'translate(-50%, -20%) scale(0.8)' :
'translate(-50%, 0) scale(1)',
zIndex: 2,
textAlign: 'center',
opacity: scrollProgress > 85 ? 0 : 1,
transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1)',
},
title: {
fontSize: '72px',
fontWeight: '800',
marginBottom: '10px',
background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
WebkitBackgroundClip: 'text',
WebkitTextFillColor: 'transparent',
transform: `translateY(${scrollProgress * -0.5}px)`,
opacity: Math.max(1 - scrollProgress * 0.005, 0),
},
subtitle: {
fontSize: '34px',
color: 'rgba(255,255,255,0.8)',
maxWidth: '600px',
margin: '0 auto',
transform: `translateY(${scrollProgress * -0.3}px)`,
opacity: Math.max(1 - scrollProgress * 0.005, 0),
},
phoneContainer: {
position: 'fixed',
top: '60%',
left: '50%',
transform: isPhoneMinimized
? 'translate(-50%, -90%) scale(0.5)'
: showPhone
? 'translate(-50%, -50%) scale(1)'
: 'translate(-50%, 100vh) scale(1)',
perspective: '2000px',
zIndex: 2,
transition: 'all 1.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
opacity: scrollProgress > 80 ? 0 : 1,
},
phone: {
position: 'relative',
height: '600px',
width: 'auto',
aspectRatio: '9/19',
maxHeight: '85vh',
transformStyle: 'preserve-3d',
transform: isPhoneMinimized ?
'rotateX(10deg) rotateY(0deg)' :
`rotateX(${Math.min(scrollProgress * 0.1, 10)}deg) rotateY(${(mousePosition.x - window.innerWidth / 2) * 0.02}deg)`,
transition: 'transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
},
phoneFrame: {
position: 'relative',
height: '100%',
width: '100%',
padding: '10px',
background: 'linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))',
borderRadius: '50px',
boxShadow: `
0 20px 40px rgba(0,0,0,0.3),
0 0 0 1px rgba(255,255,255,0.1),
inset 0 0 0 1px rgba(255,255,255,0.05)
`,
opacity: isLoaded ? 1 : 0,
transform: isLoaded ? 'translateY(0)' : 'translateY(100vh)',
transition: 'all 1.5s cubic-bezier(0.16, 1, 0.3, 1)',
},  
featureCard: {
position: 'relative',
background: 'rgba(255,59,48,0.05)',
borderRadius: '24px',
padding: '30px',
backdropFilter: 'blur(10px)',
border: '1px solid rgba(255,59,48,0.05)',
borderRadius: '24px',
padding: '30px',
backdropFilter: 'blur(10px)',
border: '1px solid rgba(255,59,48,0.2)',
cursor: 'pointer',
transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
display: 'flex',
flexDirection: 'column',
alignItems: 'center',
width: '100%',
maxWidth: '400px',
margin: '0 auto',
overflow: 'hidden',
transform: 'translateY(0px)',
},

featureCardActive: {
background: 'rgba(255,59,48,0.1)',
transform: 'translateY(-10px) scale(1.03)',
boxShadow: `
0 20px 40px rgba(255,59,48,0.2),
0 0 0 1px rgba(255,59,48,0.3),
0 0 40px rgba(255,59,48,0.1)
`,
border: '1px solid rgba(255,59,48,0.4)',
},

featureVideo: {
width: '100%',
aspectRatio: '8.2/16',
objectFit: 'cover',
borderRadius: '16px',
marginBottom: '20px',
transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
transform: 'scale(1)',
border: '1px solid rgba(255,59,48,0.2)',
boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
},

featureVideoActive: {
transform: 'scale(1.07)',
borderRadius: '20px',
border: '1px solid rgba(255,59,48,0.4)',
boxShadow: '0 15px 30px rgba(255,59,48,0.2)',
marginTop:"20px",
},

featureContent: {
width: '100%',
textAlign: 'center',
transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
transform: 'translateY(0)',
},

featureIcon: {
width: '64px',
height: '64px',
borderRadius: '20px',
background: 'linear-gradient(135deg, rgba(255,59,48,0.8), rgba(255,59,48,0.4))',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
marginBottom: '20px',
transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
transform: 'scale(1)',
},

featureIconActive: {
transform: 'scale(1.1)',
marginTop: '35px',
marginLeft:"40%"  
},

featureTitle: {
fontSize: '24px',
fontWeight: '600',
marginBottom: '12px',
color: '#fff',
},

featureDescription: {
fontSize: '16px',
lineHeight: 1.6,
color: 'rgba(255,255,255,0.7)',
},

overlay: {
position: 'absolute',
top: 0,
left: 0,
right: 0,
bottom: 0,
opacity: 0,
transition: 'opacity 0.5s ease',
borderRadius: '24px',
pointerEvents: 'none',
},

overlayActive: {
opacity: 1,
},

phoneScreen: {
width: '100%',
height: '100%',
borderRadius: '42px',
overflow: 'hidden',
position: 'relative',
boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.1)',
},
screenContent: {
width: '100%',
height: '100%',
objectFit: 'cover',
transform: isLoaded ? 'scale(1)' : 'scale(1.1)',
opacity: isLoaded ? 1 : 0,
transition: 'all 1s cubic-bezier(0.34, 1.56, 0.64, 1)',
},
screenOverlay: {
position: 'absolute',
inset: 0,
background: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0) 20%, rgba(0,0,0,0) 80%, rgba(0,0,0,0.2) 100%)',
zIndex: 2,
},
features: {
position: 'relative',
padding: '20vh 0 100px', // Adjusted padding
width: '100vw',
minWidth: '100vw',
maxWidth: '100vw',
margin: 0,
zIndex: 2,
opacity: scrollProgress > 90 ? 1 : 0,
transform: `translateY(${Math.max(0, 50 - scrollProgress)}px)`,
transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1)',
display: 'flex',
flexDirection: 'column',
alignItems: 'center',
},

// Update the feature grid to better handle the video layout
featureGrid: {
display: 'grid',
gap: '30px',
width: '100%',
padding: '20px',
maxWidth: '1920px',
opacity: scrollProgress > 30 ? 1 : 0,
transform: `translateY(${Math.max(50 - scrollProgress/2, 0)}px)`,
transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1)',
gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', // Increased minimum width
justifyItems: 'center', // Center items in grid
},

contactSection: {
padding: '100px 40px',
maxWidth: '800px',
margin: '0 auto',
textAlign: 'center',
},
contactForm: {
display: 'flex',
flexDirection: 'column',
gap: '20px',
marginTop: '40px',
},
input: {
background: 'rgba(255,255,255,0.05)',
border: '1px solid rgba(255,255,255,0.1)',
borderRadius: '12px',
padding: '16px',
color: '#fff',
fontSize: '16px',
width: '100%',
},
submitButton: {
background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
border: 'none',
borderRadius: '12px',
padding: '16px 32px',
color: '#fff',
fontSize: '16px',
fontWeight: '600',
cursor: 'pointer',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
gap: '10px',
},
progressBar: {
position: 'fixed',
bottom: 0,
left: 0,
right: 0,
height: '4px',
background: 'rgba(255,255,255,0.1)',
zIndex: 1000,
},
progressFill: {
height: '100%',
width: '90%',
background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
position: 'relative',
},
progressTextBar: {
position: 'absolute',
right: '10px',
bottom: '10px',
fontSize: '14px',
color: 'rgba(255,255,255,0.8)',
},
expandedFeature: {
position: 'fixed',
top: '50%',
left: '50%',
transform: activeFeature ?
'translate(-50%, -50%) scale(1)' :
'translate(-50%, -50%) scale(0.9)',
width: '90vw',
maxWidth: '1200px',
height: '80vh',
background: 'rgba(20,20,20,0.98)',
backdropFilter: 'blur(30px)',
borderRadius: '30px',
padding: '40px',
zIndex: 1000,
opacity: activeFeature ? 1 : 0,
visibility: activeFeature ? 'visible' : 'hidden',
transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
boxShadow: '0 50px 100px rgba(0,0,0,0.5)',
},
};

const handleSubmit = (e) => {
e.preventDefault();
// Handle form submission logic here
console.log('Submitted:', { email, message });
setEmail('');
setMessage('');
};

const openFeature = (feature) => {
setActiveFeature(feature);
setIsPhoneMinimized(true);
// Verzögerte Animation starten
setTimeout(() => setShowModalAnimation(true), 100);
};

return (
<div style={styles.container}>
{/* Enhanced Page Header */}
<EnhancedPageHeader />

{/* Section Indicator */}


{/* Home section */}
<div id="home" ref={homeRef} style={{ width: '100%', height: '100vh' }}>
{/* Video Intro - Replacing the Coming Soon text */}
<div style={styles.videoIntro}>
<video 
src={introVideo} 
style={styles.videoWrapper}
autoPlay 
muted 
playsInline
loop
/>

{/* Scroll prompt */}
<div style={styles.scrollPrompt}>
<ChevronDown size={48} color="#fff" style={{ animation: 'bounce 2s infinite' }} />
<p style={{ ...styles.featureTitle, fontSize: 15 }}>
(Scroll Down)
</p>
</div>
</div>

<div style={styles.scrollProgress}>
<div style={styles.progressText}>
<span style={styles.progressNumber}>
{Math.min(Math.round(scrollProgress), 100)}%
</span>
</div>
</div>

<div ref={explosionRef} style={styles.explosionEffect} />

<div style={styles.titleContainer} ref={titleRef}>
<h1 style={styles.title}>Sunrise Lager App</h1>
<p style={styles.subtitle}>
"It has never been so easy"
</p>
</div>

<div style={styles.phoneContainer}>
<div style={styles.phone} ref={phoneRef}>
<div style={styles.phoneFrame}>
<div style={styles.phoneScreen}>
  <EdgeCompatibleVideoPlayer
    src={videophone1}
    style={styles.phoneScreen}
    autoPlay
    loop
    muted
    playsInline
  />
  <div style={styles.screenOverlay} />
</div>
</div>
</div>
</div>
</div>

{/* Spacer div for transition */}
<div style={{ height: '100vh' }} />

{/* User Profile Section with Scroll Carousel */}
<EnhancedUserProfileSection />

{/* Breaking News Download Section */}
<BreakingNewsDownloadSection />

{/* Features Section with adjusted spacing */}
<section 
id="features" 
ref={featuresListRef}
style={{
...styles.features,
marginTop: '-45vh',
paddingTop: '10vh',
position: 'relative',
zIndex: 2
}}
>
<SectionHeader title="Features" id="features" icon={<Layers size={20} color="#FFFFFF" />} />

<div style={styles.featureGrid}>
{features.map((feature, index) => (
<div
key={index}
style={{
  ...styles.featureCard,
  ...(hoveredFeature === index ? styles.featureCardActive : {}),
}}
onClick={() => openFeature(feature)}
onMouseEnter={() => setHoveredFeature(index)}
onMouseLeave={() => setHoveredFeature(null)}
>
<div style={styles.featureContent}>
  <EdgeCompatibleVideoPlayer
    src={feature.video}
    style={{
      ...styles.featureVideo,
      ...(hoveredFeature === index ? styles.featureVideoActive : {}),
    }}
    muted
    loop
    playsInline
    autoPlay={hoveredFeature === index}
  />
  
  <div style={{
    ...styles.featureIcon,
    ...(hoveredFeature === index ? styles.featureIconActive : {}),
  }}>
    {feature.icon}
  </div>

  <h3 style={{
    ...styles.featureTitle,
    color: hoveredFeature === index ? '#fff' : 'rgba(255,255,255,0.9)',
  }}>
    {feature.title}
  </h3>

  <p style={{
    ...styles.featureDescription,
    color: hoveredFeature === index ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.7)',
  }}>
    {feature.description}
  </p>
</div>

<div style={{
  ...styles.overlay,
  ...(hoveredFeature === index ? styles.overlayActive : {}),
}} />
</div>
))}
</div>
</section>

{/* Contact Section */}
<ContactSection />

{/* Footer */}
<Footer />

<div style={styles.progressBar}>
<div style={styles.progressFill} />
<div style={styles.progressTextBar}>In Entwicklung - 90%</div>
</div>

{activeFeature && (
<div style={{
position: 'fixed',
inset: 0,
background: 'linear-gradient(135deg, rgba(0,0,0,0.97), rgba(20,20,20,0.97))',
backdropFilter: 'blur(30px)',
zIndex: 1000,
display: 'flex',
flexDirection: 'column',
opacity: showModalAnimation ? 1 : 0,
transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
}}>
{/* Enhanced Background Effects */}
<div style={{
position: 'absolute',
inset: 0,
overflow: 'hidden',
zIndex: 0,
}}>
<div style={{
position: 'absolute',
top: '10%',
left: '45%',
width: '300px',
height: '300px',
background: `radial-gradient(circle, ${activeFeature.color}20 0%, transparent 70%)`,
filter: 'blur(50px)',
transform: showModalAnimation ? 'scale(1)' : 'scale(0.5)',
opacity: showModalAnimation ? 1 : 0,
transition: 'all 1.2s cubic-bezier(0.16, 1, 0.3, 1)',
}} />
<div style={{
position: 'absolute',
bottom: '20%',
right: '35%',
width: '250px',
height: '250px',
background: `radial-gradient(circle, ${activeFeature.color}15 0%, transparent 70%)`,
filter: 'blur(40px)',
transform: showModalAnimation ? 'scale(1)' : 'scale(0.5)',
opacity: showModalAnimation ? 0.8 : 0,
transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1) 0.2s',
}} />
</div>

{/* Enhanced Header Section */}
<div style={{
position: 'absolute',
top: '24px',
width: '100%',
display: 'flex',
justifyContent: 'space-between',
padding: '0 24px',
zIndex: 10,
}}>
{/* Enhanced What's New Toggle */}
<button
onClick={() => setShowWhatsNew(!showWhatsNew)}
style={{
  padding: showWhatsNew ? '8px 16px' : '8px',
  background: showWhatsNew ? 
    'linear-gradient(135deg, rgba(255,59,48,0.3), rgba(255,45,85,0.3))' : 
    'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '24px',
  color: '#fff',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
  transform: showWhatsNew ? 'scale(1.05)' : 'scale(1)',
  boxShadow: showWhatsNew ? '0 4px 12px rgba(255,59,48,0.2)' : 'none',
}}
>
<Sparkles 
  size={20} 
  style={{
    transform: showWhatsNew ? 'rotate(360deg) scale(1.2)' : 'rotate(0) scale(1)',
    transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
    color: showWhatsNew ? '#FF3B30' : '#fff'
  }}
/>
<span style={{
  display: window.innerWidth > 768 ? 'block' : 'none',
  transition: 'all 0.3s ease'
}}>
  {showWhatsNew ? 'Back to New' : "What changed?"}
</span>
</button>

{/* Enhanced Close Button */}
<button
onClick={closeFeature}
style={{
  width: '48px',
  height: '48px',
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '50%',
  color: '#fff',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transform: 'rotate(0deg)',
  transition: 'all 0.3s ease',
}}
onMouseEnter={(e) => {
  e.currentTarget.style.transform = 'rotate(90deg)';
  e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
}}
onMouseLeave={(e) => {
  e.currentTarget.style.transform = 'rotate(0deg)';
  e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
}}
>
<X size={24} />
</button>
</div>

{/* Enhanced Video Section */}
<div style={{
position: 'relative',
width: '100%',
height: '50vh',
display: 'flex',
justifyContent: 'center',
alignItems: 'center',
padding: '20px',
'@media (min-width: 768px)': {
height: '70vh',
}
}}>
{(activeFeature.title === "Admin Dashboard" && window.innerWidth >= 1024 && !showMobileView) ? (    // Laptop Frame for Admin Dashboard on large screens
<div style={{
  position: 'relative',
  width: '50%',
  maxWidth: '700px',
  aspectRatio: '15/10',
  padding: '12px 5px 20px 5px',
  background: 'linear-gradient(145deg, rgba(255,255,255,0.2), rgba(255,255,255,0.09))',
  borderRadius: '16px',
  boxShadow: `
    0 20px 40px rgba(0,0,0,0.9),
    0 0 0 1px rgba(255,255,255,0.2),
    inset 0 0 0 1px rgba(255,255,255,0.05)
  `,
  transform: showModalAnimation ? 'translateY(0) scale(1)' : 'translateY(10px) scale(0.95)',
  opacity: showModalAnimation ? 1 : 0,
  transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
}}>
  
  {/* Laptop Screen */}
  <div style={{
    width: '98%',
    left:"8px",
    height: '100%',
    borderRadius: '8px',
    position: 'relative',
    boxShadow: 'inset 0 0 0 0px rgba(255,255,255,0.1)',
  }}>
    <video
      key={showWhatsNew ? 'old' : 'new'}
      src={
        window.innerWidth >= 1024
          ? (showWhatsNew ? activeFeature.oldLaptopVideo || activeFeature.oldVideo : activeFeature.laptopVideo || activeFeature.video)
          : (showWhatsNew ? activeFeature.oldVideo : activeFeature.video)
      }
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transform: `scale(${showWhatsNew ? 1 : 1.01})`,
        transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
      }}
      autoPlay
      loop
      muted
      playsInline
    />
    {/* Screen Overlay */}
    <div style={{
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0) 20%, rgba(0,0,0,0) 80%, rgba(0,0,0,0.2) 100%)',
      zIndex: 2,
      pointerEvents: 'none',
    }} />
  </div>
  {/* Laptop Base */}
  <div style={{
    position: 'absolute',
    bottom: -15,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '120%',
    height: '15px',
    background: 'linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))',
    borderRadius: '0 0 8px 8px',
    boxShadow: `
      0 5px 15px rgba(0,0,0,0.2),
      inset 0 0 0 1px rgba(255,255,255,0.05)
    `,
  }} />
</div>
) : (
  <div style={{
    position: 'relative',
    height: '100%',
    width: 'auto',
    aspectRatio: '9/19',
    maxHeight: '85vh',
    padding: '7px',
    background: 'linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))',
    borderRadius: '40px',
    boxShadow: '0 20px 40px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1)',
    transform: showModalAnimation ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.95)',
    opacity: showModalAnimation ? 1 : 0,
    transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
  }}>
    <div style={{
      width: '100%',
      height: '100%',
      borderRadius: '32px',
      overflow: 'hidden',
      position: 'relative',
    }}>
      {/* Video Transition Container */}
      <div style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }}>
        <video
          key={showWhatsNew ? 'old' : 'new'}
          src={showWhatsNew ? activeFeature.oldVideo : activeFeature.video}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: `scale(${showWhatsNew ? 1 : 1.01})`,
            opacity: 1,
            transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
          autoPlay
          loop
          muted
          playsInline
        />
        {/* Video Overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0) 20%, rgba(0,0,0,0) 80%, rgba(0,0,0,0.2) 100%)',
          pointerEvents: 'none',
        }} />
      </div>
    </div>
  </div>
)}
</div>
{activeFeature.title === "Admin Dashboard" && window.innerWidth >= 1024 && (
<div style={{
position: 'absolute',
top: '24px',
left: '50%',
transform: 'translateX(-50%)',
zIndex: 10,
}}>
<div style={{
  display: 'flex',
  background: 'rgba(255,255,255,0.03)',
  borderRadius: '20px',
  padding: '4px',
  border: '1px solid rgba(255,255,255,0.1)',
  gap: '4px',
}}>
  <button
    onClick={() => setShowMobileView(false)}
    style={{
      padding: '8px 16px',
      background: !showMobileView ? 
        'linear-gradient(135deg, rgba(255,59,48,0.2), rgba(255,45,85,0.2))' : 
        'transparent',
      border: 'none',
      borderRadius: '16px',
      color: '#fff',
      fontSize: '14px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.3s ease',
      transform: !showMobileView ? 'scale(1.02)' : 'scale(1)',
    }}
  >
    <Laptop size={16} style={{
      opacity: !showMobileView ? 1 : 0.6,
      transition: 'opacity 0.3s ease'
    }} />
    <span style={{
      opacity: !showMobileView ? 1 : 0.6,
      transition: 'opacity 0.3s ease'
    }}>
      Desktop
    </span>
  </button>
  <button
    onClick={() => setShowMobileView(true)}
    style={{
      padding: '8px 16px',
      background: showMobileView ? 
        'linear-gradient(135deg, rgba(255,59,48,0.2), rgba(255,45,85,0.2))' : 
        'transparent',
      border: 'none',
      borderRadius: '16px',
      color: '#fff',
      fontSize: '14px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.3s ease',
      transform: showMobileView ? 'scale(1.02)' : 'scale(1)',
    }}
  >
    <Smartphone size={16} style={{
      opacity: showMobileView ? 1 : 0.6,
      transition: 'opacity 0.3s ease'
    }} />
    <span style={{
      opacity: showMobileView ? 1 : 0.6,
      transition: 'opacity 0.3s ease'
    }}>
      Mobile
    </span>
  </button>
</div>
</div>
)}

{/* Updated responsive Visit Button */}
{activeFeature.title === "Admin Dashboard" && (
<div style={{
position: 'absolute',
top: '80px',
left: '24px',
zIndex: 10,
opacity: showModalAnimation ? 1 : 0,
transform: showModalAnimation ? 'translateY(0)' : 'translateY(-10px)',
transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1) 0.2s',
}}>
<a
  href="https://lager-app.sunrise-avengers.ch/"
  target="_blank"
  rel="noopener noreferrer"
  style={{
    padding: window.innerWidth > 768 ? '8px 16px' : '8px',
    background: 'linear-gradient(135deg, rgba(255,59,48,0.1), rgba(255,45,85,0.1))',
    border: '1px solid rgba(255,59,48,0.2)',
    borderRadius: '24px',
    color: '#fff',
    textDecoration: 'none',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    width: 'fit-content',
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255,59,48,0.2), rgba(255,45,85,0.2))';
    e.currentTarget.style.transform = 'translateY(-2px)';
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255,59,48,0.1), rgba(255,45,85,0.1))';
    e.currentTarget.style.transform = 'translateY(0)';
  }}
>
  <ExternalLink size={16} />
  <span style={{
    display: window.innerWidth > 768 ? 'block' : 'none'
  }}>
    Visit Admin Dashboard
  </span>
</a>
</div>
)}

{/* Enhanced Content Section */}
<div style={{
flex: 1,
padding: '0 20px 40px',
maxWidth: '1000px',
margin: '0 auto',
width: '100%',
overflow: 'auto',
position: 'relative',
}}>
{/* Enhanced Title Section */}
<div style={{
textAlign: 'center',
marginBottom: '40px',
transform: showModalAnimation ? 'translateY(0)' : 'translateY(20px)',
opacity: showModalAnimation ? 1 : 0,
transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.3s',
}}>
<div style={{
  display: 'inline-flex',
  alignItems: 'center',
  gap: '16px',
  padding: '8px',background: showWhatsNew ? 
  'linear-gradient(135deg, rgba(255,59,48,0.1), rgba(255,45,85,0.1))' :
  'rgba(255,255,255,0.03)',
borderRadius: '20px',
marginBottom: '24px',
transition: 'all 0.3s ease',
}}>
<div style={{
  width: '40px',
  height: '40px',
  borderRadius: '12px',
  background: `linear-gradient(135deg, ${activeFeature.color}, ${activeFeature.color}88)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transform: showWhatsNew ? 'scale(1.1)' : 'scale(1)',
  transition: 'all 0.3s ease',
}}>
  {React.cloneElement(activeFeature.icon, { 
    size: 24,
    style: {
      transform: showWhatsNew ? 'rotate(360deg)' : 'rotate(0deg)',
      transition: 'all 0.3s ease',
    }
  })}
</div>
<span style={{
  color: activeFeature.color,
  fontSize: '20px',
  fontWeight: '500',
  paddingRight: '12px',
}}>
  {activeFeature.title}
</span>
</div>

<p style={{
fontSize: '20px',
lineHeight: 1.6,
color: 'rgba(255,255,255,0.9)',
}}>
{activeFeature.description}
</p>
</div>

{/* Enhanced Grid Section with Animation */}
<div style={{
display: 'grid',
gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
gap: '24px',
opacity: showModalAnimation ? 1 : 0,
transform: showModalAnimation ? 'translateY(0)' : 'translateY(20px)',
transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.4s',
}}>
{(showWhatsNew ? activeFeature.whatsnew : activeFeature.details).map((item, index) => (
<div key={index} style={{
  background: showWhatsNew ?
    'linear-gradient(135deg, rgba(255,59,48,0.05), rgba(255,45,85,0.05))' :
    'rgba(255,255,255,0.03)',
  borderRadius: '24px',
  padding: '24px',
  border: showWhatsNew ?
    '1px solid rgba(255,59,48,0.1)' :
    '1px solid rgba(255,255,255,0.05)',
  transform: `translateY(${showModalAnimation ? '0' : '30px'})`,
  opacity: showModalAnimation ? 1 : 0,
  transition: `all 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) ${0.4 + index * 0.1}s`,
}}>
  <div style={{
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
  }}>
    {showWhatsNew ? (
      <div style={{
        width: '24px',
        height: '24px',
        borderRadius: '12px',
        background: 'linear-gradient(135deg, #FF3B30, #FF2D55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transform: 'scale(1)',
        transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}>
        <Sparkles size={12} color="#fff" />
      </div>
    ) : (
      <span style={{
        width: '6px',
        height: '6px',
        borderRadius: '3px',
        background: activeFeature.color,
        marginTop: '10px',
        flexShrink: 0,
      }} />
    )}
    <p style={{
      fontSize: '16px',
      lineHeight: 1.6,
      color: 'rgba(255,255,255,0.9)',
    }}>
      {item}
    </p>
  </div>
</div>
))}
</div>
</div>
</div>
)}

<style jsx global>{`
@keyframes bounce {
0%, 20%, 50%, 80%, 100% {
transform: translateY(0);
}
40% {
transform: translateY(-30px);
}
60% {
transform: translateY(-15px);
}
}

body {
margin: 0;
padding: 0;
font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
background: #000;
overflow-x: hidden;
}

* {
box-sizing: border-box;
}

::-webkit-scrollbar {
width: 8px;
}

::-webkit-scrollbar-track {
background: #000;
}

::-webkit-scrollbar-thumb {
background: rgba(255,59,48,0.2);
border-radius: 4px;
}

@keyframes pulse {
0% {
opacity: 1;
transform: scale(1);
}
50% {
opacity: 0.8;
transform: scale(0.98);
}
100% {
opacity: 1;
transform: scale(1);
}
}

@keyframes float {
0%, 100% {
transform: translateY(0) scale(1);
}
50% {
transform: translateY(-20px) scale(1.05);
}
}

@keyframes glow {
0%, 100% {
opacity: 0.5;
}
50% {
opacity: 1;
}
}

@keyframes fadeIn {
from {
opacity: 0;
}
to {
opacity: 1;
}
}

.breaking-news-bg {
animation: glow 4s infinite ease-in-out;
}

input::placeholder {
color: rgba(255,255,255,0.3);
}

button:hover:not(:disabled) {
transform: scale(1.02) !important;
}

button:active:not(:disabled) {
transform: scale(0.98) !important;
}
`}</style>
{/* UserManagementDashboard Modal */}
{showUserManagement && (
<div style={{
position: 'fixed',
top: 0,
left: 0,
right: 0,
bottom: 0,
backgroundColor: 'rgba(0,0,0,0.9)',
backdropFilter: 'blur(5px)',
zIndex: 2000, 
display: 'flex',
justifyContent: 'center',
alignItems: 'center',
animation: 'fadeIn 0.3s ease',
overflow: 'auto'
}}>
<UserManagementDashboard 
onClose={() => {
setShowUserManagement(false);
document.body.style.overflow = ''; // Restore scrolling when modal is closed
}} 
onLoginStatusChange={handleLoginStatusChange}
darkMode={false}
/>
</div>
)}
{showPrivacyPolicy && (
<div style={{
position: 'fixed',
top: 0,
left: 0,
right: 0,
bottom: 0,
backgroundColor: 'rgba(0,0,0,0.85)',
backdropFilter: 'blur(5px)',
zIndex: 2000,
display: 'flex',
justifyContent: 'center',
alignItems: 'center',
animation: 'fadeIn 0.3s ease',
overflow: 'auto'
}}>
<div style={{
maxWidth: '800px',
width: '90%',
maxHeight: '90vh',
background: 'rgba(28, 28, 30, 0.95)',
borderRadius: '20px',
overflow: 'hidden',
boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
backdropFilter: 'blur(20px)',
border: '1px solid rgba(255, 255, 255, 0.1)',
}}>
{/* Modal Header */}
<div style={{
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center',
padding: '20px 24px',
borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
}}>
<h3 style={{
margin: 0,
color: '#fff',
fontSize: '20px',
fontWeight: '600',
display: 'flex',
alignItems: 'center',
gap: '12px'
}}>
<Shield size={24} style={{ color: '#FF3B30' }} />
Privacy Notice
</h3>
<button 
style={{
  width: '36px',
  height: '36px',
  borderRadius: '10px',
  background: 'rgba(255, 255, 255, 0.05)',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'rgba(255, 255, 255, 0.7)',
  cursor: 'pointer',
  transition: 'all 0.2s',
}}
onClick={() => setShowPrivacyPolicy(false)}
>
<X size={20} />
</button>
</div>

{/* Modal Content - Scrollable */}
<div style={{
padding: '20px',
maxHeight: 'calc(90vh - 150px)',
overflowY: 'auto',
color: 'rgba(255, 255, 255, 0.7)',
lineHeight: 1.6,
fontSize: '14px',
}}>
<p>
With the following Privacy Notice, Sunrise LLC (hereinafter "Sunrise" "us" or "we") informs you about which personal data (hereinafter "Data") is collected and processed when using our website, obtaining services as a customer (concluded service contract) or receiving promotions (e.g. newsletter, giveaways). This Privacy Notice also applies to the services offered under the brand names yallo, Lebara, MySports, swype and UPC.
</p>
<p>
Please note that this Privacy Notice may be updated from time to time. We therefore recommend that you check our website regularly for any updates.
</p>

<h4 style={{
fontSize: '18px',
fontWeight: '600',
color: '#fff',
marginTop: '24px',
marginBottom: '16px',
}}>1. Processing Purposes</h4>
<p>
The available Data will primarily be used by us to fulfill our contractual obligations, to acquire new customers, to ensure the smoothest possible operation of our services, to contribute to the improvement of our services, to enable marketing and analyses, and to comply with our legal and regulatory obligations, this includes in particular:
</p>

<h5 style={{
fontSize: '16px',
fontWeight: '600',
color: '#fff',
marginTop: '16px',
marginBottom: '12px',
}}>1.1 Conclusion and performance of the contract</h5>
<ul style={{
paddingLeft: '24px',
marginBottom: '16px',
}}>
<li style={{ marginBottom: '8px' }}>Verification of the conditions for concluding a contract;</li>
<li style={{ marginBottom: '8px' }}>Performance of contractual obligations;</li>
<li style={{ marginBottom: '8px' }}>Processing and responding to inquiries or complaints;</li>
<li style={{ marginBottom: '8px' }}>Ensuring customer and user administration;</li>
<li style={{ marginBottom: '8px' }}>Invoicing and, if necessary, the consultation of a debt collection agency.</li>
</ul>

<h5 style={{
fontSize: '16px',
fontWeight: '600',
color: '#fff',
marginTop: '16px',
marginBottom: '12px',
}}>1.2 Data processing in our legitimate interest</h5>
<ul style={{
paddingLeft: '24px',
marginBottom: '16px',
}}>
<li style={{ marginBottom: '8px' }}>Evaluation of your usage, ordering or inquiry behavior;</li>
<li style={{ marginBottom: '8px' }}>Updating and validating your Data;</li>
<li style={{ marginBottom: '8px' }}>Information about our services, products and promotions;</li>
<li style={{ marginBottom: '8px' }}>Prevention of unlawful use of services;</li>
<li style={{ marginBottom: '8px' }}>Maintenance, development and preservation of customer relations;</li>
<li style={{ marginBottom: '8px' }}>Individualizing services and providing personalized content;</li>
<li style={{ marginBottom: '8px' }}>Direct marketing and analysis purposes.</li>
</ul>

<p style={{ marginTop: '24px' }}>
Last update: January 2024
</p>

<p style={{ marginTop: '24px', marginBottom: '16px' }}>
Further legal information can be found at:  
<a 
  href="https://www.sunrise.ch/en/general/legal-matters/data-protection" 
  target="_blank" 
  rel="noopener noreferrer"
  style={{
    color: '#FF3B30',
    textDecoration: 'none',
    marginLeft: '6px',
    transition: 'color 0.2s',
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.color = '#FF5E54';
    e.currentTarget.style.textDecoration = 'underline';
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.color = '#FF3B30';
    e.currentTarget.style.textDecoration = 'none';
  }}
>
  Sunrise Privacy Policy
</a>
</p>
</div>

{/* Modal Footer */}
<div style={{
padding: '16px 24px',
borderTop: '1px solid rgba(255, 255, 255, 0.1)',
display: 'flex',
justifyContent: 'flex-end',
}}>
<button 
style={{
  padding: '12px 24px',
  background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
  border: 'none',
  borderRadius: '12px',
  color: '#FFFFFF',
  fontSize: '16px',
  fontWeight: '600',
  cursor: 'pointer',
  transition: 'all 0.2s',
}}
onClick={() => setShowPrivacyPolicy(false)}
onMouseEnter={(e) => {
  e.currentTarget.style.transform = 'translateY(-2px)';
  e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 59, 48, 0.3)';
}}
onMouseLeave={(e) => {
  e.currentTarget.style.transform = 'translateY(0)';
  e.currentTarget.style.boxShadow = 'none';
}}
>
I Understand
</button>
</div>
</div>
</div>
)}

{/* Terms of Service Modal */}
{showTerms && (
<div style={{
position: 'fixed',
top: 0,
left: 0,
right: 0,
bottom: 0,
backgroundColor: 'rgba(0,0,0,0.85)',
backdropFilter: 'blur(5px)',
zIndex: 2000,
display: 'flex',
justifyContent: 'center',
alignItems: 'center',
animation: 'fadeIn 0.3s ease',
overflow: 'auto'
}}>
<div style={{
maxWidth: '800px',
width: '90%',
maxHeight: '90vh',
background: 'rgba(28, 28, 30, 0.95)',
borderRadius: '20px',
overflow: 'hidden',
boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
backdropFilter: 'blur(20px)',
border: '1px solid rgba(255, 255, 255, 0.1)',
}}>
{/* Modal Header */}
<div style={{
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center',
padding: '20px 24px',
borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
}}>
<h3 style={{
margin: 0,
color: '#fff',
fontSize: '20px',
fontWeight: '600',
display: 'flex',
alignItems: 'center',
gap: '12px'
}}>
<FileText size={24} style={{ color: '#FF3B30' }} />
Terms of Service
</h3>
<button 
style={{
  width: '36px',
  height: '36px',
  borderRadius: '10px',
  background: 'rgba(255, 255, 255, 0.05)',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'rgba(255, 255, 255, 0.7)',
  cursor: 'pointer',
  transition: 'all 0.2s',
}}
onClick={() => setShowTerms(false)}
>
<X size={20} />
</button>
</div>

{/* Modal Content - Scrollable */}
<div style={{
padding: '20px',
maxHeight: 'calc(90vh - 150px)',
overflowY: 'auto',
}}>
<h4 style={{
fontSize: '18px',
fontWeight: '600',
color: '#fff',
marginTop: 0,
marginBottom: '16px',
}}>1. Acceptance of Terms</h4>
<p style={{
color: 'rgba(255, 255, 255, 0.7)',
lineHeight: 1.6,
marginBottom: '24px',
}}>
By using the cAmPP ("App"), you (the "User") accept the following terms of service. If you do not agree to these terms, you may not use the App.
</p>

<h4 style={{
fontSize: '18px',
fontWeight: '600',
color: '#fff',
marginBottom: '16px',
}}>2. Registration and Account</h4>
<p style={{
color: 'rgba(255, 255, 255, 0.7)',
lineHeight: 1.6,
marginBottom: '24px',
}}>
To use the App, you must create an account and register. You are responsible for maintaining the confidentiality of your account information and for all activities that occur under your account.
</p>

<h4 style={{
fontSize: '18px',
fontWeight: '600',
color: '#fff',
marginBottom: '16px',
}}>3. Privacy</h4>
<p style={{
color: 'rgba(255, 255, 255, 0.7)',
lineHeight: 1.6,
marginBottom: '24px',
}}>
The collection and use of your personal data is subject to our Privacy Policy, which can be viewed in the App.
</p>

<h4 style={{
fontSize: '18px',
fontWeight: '600',
color: '#fff',
marginBottom: '16px',
}}>4. Code of Conduct</h4>
<p style={{
color: 'rgba(255, 255, 255, 0.7)',
lineHeight: 1.6,
marginBottom: '12px',
}}>
When using the App, you agree to:
</p>
<ul style={{
color: 'rgba(255, 255, 255, 0.7)',
lineHeight: 1.6,
marginBottom: '24px',
paddingLeft: '24px',
}}>
<li style={{ marginBottom: '8px' }}>Not conduct illegal or unethical activities</li>
<li style={{ marginBottom: '8px' }}>Respect the rights of other users</li>
<li style={{ marginBottom: '8px' }}>Not share content that is offensive,defamatory, or inappropriate</li>
              <li style={{ marginBottom: '8px' }}>Not misuse or disrupt the App</li>
            </ul>
            
            <h4 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#fff',
              marginBottom: '16px',
            }}>5. Limitation of Liability</h4>
            <p style={{
              color: 'rgba(255, 255, 255, 0.7)',
              lineHeight: 1.6,
              marginBottom: '24px',
            }}>
              The App is provided "as is" without any warranties. We are not liable for damages arising from the use or inability to use the App.
            </p>
            
            <h4 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#fff',
              marginBottom: '16px',
            }}>6. Changes to Terms</h4>
            <p style={{
              color: 'rgba(255, 255, 255, 0.7)',
              lineHeight: 1.6,
              marginBottom: '24px',
            }}>
              We reserve the right to change these terms of service at any time. The updated terms will be published in the App.
            </p>
            
            <h4 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#fff',
              marginBottom: '16px',
            }}>7. Termination</h4>
            <p style={{
              color: 'rgba(255, 255, 255, 0.7)',
              lineHeight: 1.6,
              marginBottom: '24px',
            }}>
              We reserve the right to terminate or suspend a user's access to the App at any time and for any reason.
            </p>

            <p style={{
              color: 'rgba(255, 255, 255, 0.7)',
              lineHeight: 1.6,
              marginBottom: '16px',
            }}>
              Further legal information can be found at:  
              <a 
                href="https://www.sunrise.ch/en/general/legal-matters/data-protection" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  color: '#FF3B30',
                  textDecoration: 'none',
                  marginLeft: '6px',
                  transition: 'color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#FF5E54';
                  e.currentTarget.style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#FF3B30';
                  e.currentTarget.style.textDecoration = 'none';
                }}
              >
                Sunrise Terms of Service
              </a>
            </p>
          </div>

          {/* Modal Footer */}
          <div style={{
            padding: '16px 24px',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}>
            <button 
              style={{
                padding: '12px 24px',
                background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
                border: 'none',
                borderRadius: '12px',
                color: '#FFFFFF',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onClick={() => setShowTerms(false)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 59, 48, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              I Understand
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
);

};

export default LandingPage;